'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { Loader2, ChevronLeft, ChevronRight, AlertCircle } from 'lucide-react';
import {
  inputClass,
  textareaClass,
  WizardPanelCard,
  WizardPanelHeader,
  WizardPanelBody,
  WizardPageHeader,
  WizardHelpBanner,
  WizardFormSection,
  SectionHeader,
  Field,
} from '@/components/event-wizard/wizard-ui';
import { useEntitlements } from '@/hooks/useEntitlements';
import { getParticipantLimitErrorMessage } from '@/config/plans.config';
import { TicketsWizardPanel, emptyTicketForm } from '@/components/event-wizard/TicketsWizardPanel';
import { notifyTicketsChanged } from '@/hooks/useEventTickets';
import type { PublishCheckResult } from '@/components/event-wizard/ReviewWizardPanel';
import {
  buildCommunicationSettingsPayload,
  buildTicketApiPayload,
  buildTicketPatchPayload,
  communicationFromSettings,
  eventHasPaidTicket,
  ticketFormFromRecord,
  validateTicketForm,
  type TicketFieldErrors,
  type TicketFormState,
} from '@/components/event-wizard/ticket-wizard-utils';
import {
  buildEventFinancePayload,
  buildOrganizerBankPayload,
  emptyFinanceForm,
  financeFormFromApi,
  mapPaymentMethodCatalog,
  PAYMENT_METHOD_OPTIONS,
  validateFinanceForm,
  type FinanceFieldErrors,
  type FinanceFormState,
  type PaymentMethodOption,
  type PayoutVerificationState,
} from '@/components/event-wizard/finance-wizard-utils';
import { EventWizardFinanceFields } from '@/components/event-wizard/EventWizardFinanceFields';
import { eventsApi, eventWorkflowApi, eventFrameworksApi, organizerFinanceApi, paymentMethodsApi, registrationFieldsApi, isOrganizerProfileRequiredError, type EventFrameworkRecord, type EventPayoutReadinessData } from '@/lib/api';
import { finishNavigationLoading } from '@/lib/navigation-loading';
import { useAuthStore } from '@/lib/auth-store';
import { resolveStorageUrl } from '@/lib/storage-url';
import { validateEventCoverImage, validateOrgBanner, validateOrgLogo } from '@/lib/upload-validation';
import { VenueLocationSelects } from '@/components/event-wizard/VenueLocationSelects';
import { CategorySelect } from '@/components/event-wizard/CategorySelect';
import { eventDiscoveryApi, type EventCategoryItem } from '@/lib/event-discovery-api';
import { useEventWizardStore } from '@/stores/event-wizard-store';
import { DateTimePicker } from '@/components/ui/DateTimePicker';
import {
  WIZARD_STEPS,
  formatApiError,
  fromDatetimeLocal,
  toDatetimeLocalValue,
  nowDatetimeLocalValue,
  validateBasicsStep,
  validateDetailsStep,

  apiValidationFieldErrors,
  firstValidationMessage,
  focusFirstInvalidField,
  emptyRulesForm,
  readPersistedWizardStep,
  persistWizardStep,
  clearPersistedWizardStep,
  isWizardBlockedStatus,
  visibilityFromEvent,
  timezoneLabel,
  DEFAULT_EVENT_TIMEZONE,
  showVenueFields,
  showOnlineLinkField,
  type EventVisibility,
} from '@/components/event-wizard/wizard-utils';
import { ParticipationRulesPanel } from '@/components/event-wizard/ParticipationRulesPanel';
import { AdvancedSettingsPanel } from '@/components/event-wizard/AdvancedSettingsPanel';
import {
  advancedSettingsFromEvent,
  buildAdvancedSettingsPayload,
  emptyAdvancedSettingsForm,
  type AdvancedSettingsFormState,
} from '@/components/event-wizard/advanced-settings-utils';
import {
  buildParticipationSettingsPayload,
  emptyParticipationForm,
  firstParticipationError,
  participationFromEvent,
  validateParticipationForm,
  type ParticipationFieldErrors,
  type ParticipationFormState,
} from '@/components/event-wizard/participation-utils';
import { isStaticExportPlaceholder, isValidEventWorkspaceId, resolveEventIdFromPath, rememberWorkspaceEventId } from '@/lib/resolve-static-param';
import type { RegistrationFieldRecord } from '@/components/event-wizard/registration-builder-utils';
import {
  connectableFormFieldId,
  mergeRegistrationFormFieldIds,
  reconcileTicketFormFields,
} from '@/components/event-wizard/connected-form-fields';
import { useDebouncedCallback } from '@/hooks/useDebouncedCallback';
import {
  trackWizardDraftAbandoned,
  trackWizardEvent,
  trackWizardStepCompleted,
  trackWizardStepViewed,
  WIZARD_ANALYTICS_EVENTS,
} from '@/lib/wizard-analytics';
import { wizardSaveStatusMessage, type WizardSaveStatus } from '@/lib/wizard-save-status';
import {
  DraftSaveQueue,
  DraftVersionConflictError,
  extractDraftVersion,
  isDraftVersionConflict,
  logDraftVersion,
  saveDraftWithConflictRetry,
  shouldAcceptDraftVersion,
} from '@/lib/event-draft-version';

const AUTO_PLACEHOLDER_SLUG_PREFIX = 'untitled-event-';

function isAutoGeneratedPlaceholderSlug(slug?: string | null): boolean {
  return typeof slug === 'string' && slug.toLowerCase().startsWith(AUTO_PLACEHOLDER_SLUG_PREFIX);
}

function isUntitledEventTitle(title?: string | null): boolean {
  if (!title) return true;
  const normalized = title.trim().toLowerCase();
  return normalized === 'untitled event' || normalized === 'untitled-event';
}

const RegistrationBuilderPanel = dynamic(
  () =>
    import('@/components/event-wizard/RegistrationBuilderPanel').then((m) => ({
      default: m.RegistrationBuilderPanel,
    })),
  {
    loading: () => (
      <div className="flex min-h-[240px] items-center justify-center rounded-2xl border border-neutral-200 bg-bg">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    ),
  },
);

const ReviewWizardPanel = dynamic(
  () =>
    import('@/components/event-wizard/ReviewWizardPanel').then((m) => ({
      default: m.ReviewWizardPanel,
    })),
  {
    loading: () => (
      <div className="flex min-h-[320px] items-center justify-center rounded-2xl border border-neutral-200 bg-bg">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    ),
  },
);

type EventRecord = Record<string, any>;

function normalizePublishCheck(payload: any): PublishCheckResult {
  const candidate = payload?.data?.data ?? payload?.data ?? payload ?? {};
  const errors = Array.isArray(candidate.errors)
    ? candidate.errors.map((item: unknown) => String(item))
    : [];
  const eventErrors = Array.isArray(candidate.event_errors)
    ? candidate.event_errors.map((item: unknown) => String(item))
    : undefined;
  const accountErrors = Array.isArray(candidate.account_errors)
    ? candidate.account_errors.map((item: unknown) => String(item))
    : undefined;
  const fieldErrors =
    candidate.field_errors && typeof candidate.field_errors === 'object'
      ? candidate.field_errors
      : undefined;

  return {
    can_publish: candidate.can_publish === true,
    errors,
    event_errors: eventErrors,
    account_errors: accountErrors,
    field_errors: fieldErrors,
    registration_offer:
      typeof candidate.registration_offer === 'string' ? candidate.registration_offer : undefined,
  };
}

function publishCheckFromError(err: unknown): PublishCheckResult | null {
  const response = (err as { response?: { data?: any } })?.response;
  if (!response?.data) return null;

  const data = response.data;
  const flatErrors = Array.isArray(data?.data?.errors)
    ? data.data.errors.map((item: unknown) => String(item))
    : data?.errors && typeof data.errors === 'object'
      ? Object.values(data.errors).flat().map((item: unknown) => String(item))
      : [];

  return {
    can_publish: false,
    errors: flatErrors,
    event_errors: Array.isArray(data?.data?.event_errors)
      ? data.data.event_errors.map((item: unknown) => String(item))
      : undefined,
    account_errors: Array.isArray(data?.data?.account_errors)
      ? data.data.account_errors.map((item: unknown) => String(item))
      : undefined,
    field_errors: data?.data?.field_errors ?? data?.errors,
  };
}

function formatSavedAt(d: Date) {
  return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

const DRAFT_VERSION_CONFLICT_MESSAGE =
  'This draft was updated in another session. Your form values are kept — review the latest version and save again.';

const registrationAutoDraftLocks = new Set<string>();

export function EventWizard({
  eventId: eventIdProp,
  createMode = false,
  organizationId,
}: {
  eventId?: string;
  createMode?: boolean;
  organizationId?: string;
}) {
  const router = useRouter();
  const pathname = usePathname();
  const [localEventId, setLocalEventId] = useState<string | null>(null);
  const eventId = useMemo(() => {
    if (createMode) {
      return localEventId ?? '';
    }
    return resolveEventIdFromPath(eventIdProp ?? '', pathname);
  }, [createMode, localEventId, eventIdProp, pathname]);
  const {
    stepIndex,
    setStepIndex,
    setDraftVersion: setStoredDraftVersion,
    setEventId,
    setDraftTitle,
    eventId: storeEventId,
    draftTitle: storeDraftTitle,
  } = useEventWizardStore();
  const searchParams = useSearchParams();

  const authChecking = useAuthStore((s) => s.authChecking);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [saveStatus, setSaveStatus] = useState<WizardSaveStatus>('idle');
  const [saveErrorMessage, setSaveErrorMessage] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [stepError, setStepError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [versionConflict, setVersionConflict] = useState(false);

  const [event, setEvent] = useState<EventRecord | null>(null);
  const [draftVersion, setDraftVersion] = useState<number | null>(null);
  /** Single source of truth for optimistic locking — read at request time. */
  const versionRef = useRef<number | null>(null);
  const savingRef = useRef(false);
  const saveQueueRef = useRef(new DraftSaveQueue());
  const frameworksLoadedRef = useRef(false);
  const isLocalCreateRef = useRef(false);

  const applyDraftVersion = useCallback(
    (value: unknown, options?: { allowDowngrade?: boolean }) => {
      const parsed =
        typeof value === 'object' && value !== null
          ? extractDraftVersion(value)
          : extractDraftVersion({ version: value });
      if (
        !shouldAcceptDraftVersion(versionRef.current, parsed, {
          allowDowngrade: options?.allowDowngrade,
        })
      ) {
        if (parsed !== null && versionRef.current !== null && parsed < versionRef.current) {
          logDraftVersion({
            eventId,
            phase: 'stale-ignored',
            submittedVersion: versionRef.current,
            returnedVersion: parsed,
            step: 'applyDraftVersion',
            inFlight: saveQueueRef.current.isInFlight,
          });
          return versionRef.current;
        }
        throw new Error('Invalid draft version returned by backend.');
      }

      versionRef.current = parsed;
      setDraftVersion(parsed);
      setStoredDraftVersion(parsed);
      return parsed;
    },
    [eventId, setStoredDraftVersion],
  );

  /** Fetch server version only — does not overwrite local form fields. */
  const syncDraftVersionFromServer = useCallback(
    async (id: string) => {
      const draftRes = await eventWorkflowApi.getDraft(id, { fresh: true });
      const latest = extractDraftVersion(draftRes.data);
      if (latest === null) return null;
      // Conflict recovery: server is ahead — accept if >= local; never move backward.
      return applyDraftVersion(latest);
    },
    [applyDraftVersion],
  );

  const [publishCheck, setPublishCheck] = useState<PublishCheckResult | null>(null);

  const [basics, setBasics] = useState({
    title: '',
    description: '',
    short_description: '',
    type: 'other',
    event_framework_id: '',
    custom_event_type: '',
    mode: 'online',
    start_date: '',
    end_date: '',
    visibility: 'public' as EventVisibility,
    timezone: DEFAULT_EVENT_TIMEZONE,
    registration_type: 'individual' as 'individual' | 'team',
    contact_email: '',
    contact_phone: '',
    slug: '',
    cover_image: '',
  });

  const [details, setDetails] = useState({
    registration_start: '',
    registration_end: '',
    venue_name: '',
    venue_address: '',
    venue_city: '',
    venue_state: '',
    venue_country: '',
    venue_postal_code: '',
    online_link: '',
    max_participants: '' as string | number,
    category: '',
    attendance_defaults_json: '',
  });

  const { currentPlan } = useEntitlements();
  const participantLimitError = useMemo(() => {
    const count = details.max_participants === '' ? 0 : Number(details.max_participants);
    if (count > 0 && currentPlan) {
      return getParticipantLimitErrorMessage(currentPlan, count);
    }
    return null;
  }, [details.max_participants, currentPlan]);

  const [participation, setParticipation] = useState<ParticipationFormState>(emptyParticipationForm());
  const [participationErrors, setParticipationErrors] = useState<ParticipationFieldErrors>({});

  const [media, setMedia] = useState({
    cover_image: '',
    logo: '',
    banner: '',
  });

  const [rulesForm, setRulesForm] = useState(emptyRulesForm());
  const [advancedSettings, setAdvancedSettings] = useState<AdvancedSettingsFormState>(
    emptyAdvancedSettingsForm(),
  );
  const [stepRestored, setStepRestored] = useState(false);
  const [highestCompletedStep, setHighestCompletedStep] = useState(0);
  const [leavePromptOpen, setLeavePromptOpen] = useState(false);
  const [publishCheckLoading, setPublishCheckLoading] = useState(false);
  const [publishRedirecting, setPublishRedirecting] = useState(false);
  const [coverImageFile, setCoverImageFile] = useState<File | null>(null);
  const [coverImageProgress, setCoverImageProgress] = useState<number | null>(null);
  const [brandingCoverFile, setBrandingCoverFile] = useState<File | null>(null);
  const [brandingLogoFile, setBrandingLogoFile] = useState<File | null>(null);
  const [brandingBannerFile, setBrandingBannerFile] = useState<File | null>(null);
  const [brandingCoverProgress, setBrandingCoverProgress] = useState<number | null>(null);
  const [brandingLogoProgress, setBrandingLogoProgress] = useState<number | null>(null);
  const [brandingBannerProgress, setBrandingBannerProgress] = useState<number | null>(null);

  const [fields, setFields] = useState<RegistrationFieldRecord[]>([]);
  const [fieldsLoading, setFieldsLoading] = useState(false);
  const [fieldsReady, setFieldsReady] = useState(false);
  const fieldsLoadGen = useRef(0);
  const [tickets, setTickets] = useState<any[]>([]);
  const [ticketForm, setTicketForm] = useState<TicketFormState>(emptyTicketForm());
  const [editingTicketId, setEditingTicketId] = useState<string | null>(null);
  const [deletingTicketId, setDeletingTicketId] = useState<string | null>(null);
  const [ticketFieldErrors, setTicketFieldErrors] = useState<TicketFieldErrors & Record<string, string>>({});
  const [financeForm, setFinanceForm] = useState<FinanceFormState>(emptyFinanceForm());
  const [financeFieldErrors, setFinanceFieldErrors] = useState<FinanceFieldErrors>({});
  const [financeLoaded, setFinanceLoaded] = useState(false);
  const [financeProfileMissing, setFinanceProfileMissing] = useState(false);
  const [payoutReadiness, setPayoutReadiness] = useState<EventPayoutReadinessData | null>(null);

  const basicsRef = useRef(basics);
  const detailsRef = useRef(details);
  const participationRef = useRef(participation);
  const mediaRef = useRef(media);
  const rulesFormRef = useRef(rulesForm);
  const advancedSettingsRef = useRef(advancedSettings);
  const financeFormRef = useRef(financeForm);

  useEffect(() => {
    basicsRef.current = basics;
    detailsRef.current = details;
    participationRef.current = participation;
    mediaRef.current = media;
    rulesFormRef.current = rulesForm;
    advancedSettingsRef.current = advancedSettings;
    financeFormRef.current = financeForm;
  }, [basics, details, participation, media, rulesForm, advancedSettings, financeForm]);
  const [paymentMethodOptions, setPaymentMethodOptions] =
    useState<PaymentMethodOption[]>(PAYMENT_METHOD_OPTIONS);
  const [payoutVerification] = useState<PayoutVerificationState>({
    paymentProfileReady: true,
    kycApproved: true,
    settlementVerified: true,
    ready: true,
  });
  const [frameworks, setFrameworks] = useState<EventFrameworkRecord[]>([]);
  const [eventCategories, setEventCategories] = useState<EventCategoryItem[]>([]);
  const [eventCategoriesLoading, setEventCategoriesLoading] = useState(false);
  const [eventCategoriesError, setEventCategoriesError] = useState<string | null>(null);
  const [frameworkConfig, setFrameworkConfig] = useState<{
    framework?: EventFrameworkRecord;
    wizard_steps?: EventFrameworkRecord['wizard_steps'];
  } | null>(null);
  const [coverUploadProgress, setCoverUploadProgress] = useState<number | null>(null);
  const [coverUploadError, setCoverUploadError] = useState<string | null>(null);
  const [coverUploadKey, setCoverUploadKey] = useState(0);
  const [logoUploadProgress, setLogoUploadProgress] = useState<number | null>(null);
  const [logoUploadError, setLogoUploadError] = useState<string | null>(null);
  const [logoUploadKey, setLogoUploadKey] = useState(0);
  const [bannerUploadProgress, setBannerUploadProgress] = useState<number | null>(null);
  const [bannerUploadError, setBannerUploadError] = useState<string | null>(null);
  const [bannerUploadKey, setBannerUploadKey] = useState(0);

  const selectedFramework = useMemo(
    () => frameworks.find((f) => f.id === basics.event_framework_id) ?? frameworkConfig?.framework ?? null,
    [frameworks, basics.event_framework_id, frameworkConfig?.framework],
  );

  const customFramework = useMemo(
    () => frameworks.find((f) => f.code === 'custom') ?? null,
    [frameworks],
  );

  const isCustomOrganizing =
    !basics.event_framework_id ||
    selectedFramework?.code === 'custom' ||
    basics.event_framework_id === customFramework?.id;

  const refreshAll = useCallback(async (options?: { fresh?: boolean; eventIdOverride?: string }) => {
    const id = options?.eventIdOverride || eventId;
    if (!id || isStaticExportPlaceholder(id)) {
      setLoadError('Event not found');
      return null;
    }

    try {
      const useFresh = options?.fresh ?? true;
      const versionAtRequestStart = versionRef.current;
      const draftRes = await eventWorkflowApi.getDraft(id, { fresh: useFresh });
      const frameworkPayload = draftRes.data?.framework;
      if (frameworkPayload?.framework) {
        setFrameworkConfig(frameworkPayload);
      }

      const incomingVersion = extractDraftVersion(draftRes.data);
      // Never move version backward from a GET — stale refreshAll races are discarded.
      if (
        incomingVersion !== null &&
        versionRef.current !== null &&
        incomingVersion < versionRef.current
      ) {
        logDraftVersion({
          eventId: id,
          phase: 'stale-ignored',
          submittedVersion: versionRef.current,
          returnedVersion: incomingVersion,
          step: 'refreshAll',
          inFlight: saveQueueRef.current.isInFlight || savingRef.current,
        });
        return {
          version: versionRef.current,
          event: (draftRes.data?.data?.event as EventRecord) ?? null,
        };
      }

      // If a save completed between request start and response, prefer the newer local token
      // and do not overwrite in-progress form fields with a lagging snapshot.
      if (
        versionAtRequestStart !== null &&
        versionRef.current !== null &&
        versionRef.current > versionAtRequestStart &&
        (incomingVersion === null || incomingVersion < versionRef.current)
      ) {
        logDraftVersion({
          eventId: id,
          phase: 'stale-ignored',
          submittedVersion: versionRef.current,
          returnedVersion: incomingVersion,
          step: 'refreshAll-after-save',
          inFlight: saveQueueRef.current.isInFlight || savingRef.current,
        });
        return {
          version: versionRef.current,
          event: (draftRes.data?.data?.event as EventRecord) ?? null,
        };
      }

      const v = applyDraftVersion(incomingVersion);

      const e = draftRes.data?.data?.event as EventRecord;
      if (!e) {
        throw new Error('Event draft payload is missing event data.');
      }
      setEvent(e);

      const organizerUser = e.organizer?.user;
      const settings = (e.settings ?? {}) as Record<string, any>;

      const settingsTemplate =
        settings.template && typeof settings.template === 'object'
          ? (settings.template as Record<string, unknown>)
          : {};
      const customEventType =
        (typeof settings.custom_event_type === 'string' && settings.custom_event_type) ||
        (typeof settingsTemplate.organizer_label === 'string' && settingsTemplate.organizer_label) ||
        '';

      const loadedSlug = e.slug ?? '';
      const draftPayloadJson = (draftRes.data?.data?.payload_json ?? {}) as Record<string, any>;
      const basicsPayload = (draftPayloadJson.basics ?? {}) as Record<string, any>;
      const titleFromPayload =
        typeof basicsPayload.title === 'string' &&
        basicsPayload.title.trim() !== '' &&
        !isUntitledEventTitle(basicsPayload.title)
          ? basicsPayload.title.trim()
          : '';
      const titleFromEvent =
        typeof e.title === 'string' &&
        e.title.trim() !== '' &&
        !isUntitledEventTitle(e.title)
          ? e.title.trim()
          : '';

      setBasics((prev) => {
        const liveTitle = prev.title;
        const nextTitle =
          liveTitle && liveTitle.trim() !== '' && !isUntitledEventTitle(liveTitle)
            ? liveTitle
            : titleFromEvent || titleFromPayload || '';
        return {
          title: nextTitle,
          description: e.description ?? basicsPayload.description ?? prev.description,
          short_description:
            e.short_description ?? basicsPayload.short_description ?? prev.short_description,
          type: e.type ?? basicsPayload.type ?? prev.type ?? 'other',
          event_framework_id:
            e.event_framework_id ?? frameworkPayload?.framework?.id ?? prev.event_framework_id ?? '',
          custom_event_type: customEventType || prev.custom_event_type,
          mode: e.mode ?? basicsPayload.mode ?? prev.mode ?? 'online',
          start_date:
            toDatetimeLocalValue(e.start_date ?? basicsPayload.start_date) || prev.start_date,
          end_date: toDatetimeLocalValue(e.end_date ?? basicsPayload.end_date) || prev.end_date,
          visibility: visibilityFromEvent(e) || prev.visibility,
          timezone: e.timezone ?? basicsPayload.timezone ?? prev.timezone ?? DEFAULT_EVENT_TIMEZONE,
          registration_type:
            e.registration_type === 'team' || basicsPayload.registration_type === 'team'
              ? 'team'
              : prev.registration_type || 'individual',
          contact_email:
            e.contact_email ?? basicsPayload.contact_email ?? organizerUser?.email ?? prev.contact_email ?? '',
          contact_phone:
            e.contact_phone ?? basicsPayload.contact_phone ?? organizerUser?.phone ?? prev.contact_phone ?? '',
          slug: isAutoGeneratedPlaceholderSlug(loadedSlug) ? '' : loadedSlug || prev.slug,
          cover_image: e.cover_image ?? basicsPayload.cover_image ?? prev.cover_image ?? '',
        };
      });

      setDetails({
        registration_start: toDatetimeLocalValue(e.registration_start),
        registration_end: toDatetimeLocalValue(e.registration_end),
        venue_name: e.venue_name ?? '',
        venue_address: e.venue_address ?? '',
        venue_city: e.venue_city ?? '',
        venue_state: e.venue_state ?? '',
        venue_country: e.venue_country ?? '',
        venue_postal_code: e.venue_postal_code ?? '',
        online_link: e.online_link ?? '',
        max_participants: e.max_participants ?? '',
        category: e.category ?? '',
        attendance_defaults_json:
          settings.attendance_defaults && typeof settings.attendance_defaults === 'object'
            ? JSON.stringify(settings.attendance_defaults, null, 2)
            : '',
      });

      setParticipation(participationFromEvent(e));

      setMedia({
        cover_image: e.cover_image ?? '',
        logo: e.logo ?? '',
        banner: e.banner ?? '',
      });

      const adv = advancedSettingsFromEvent(e);
      setAdvancedSettings(adv);
      setRulesForm(adv.rules);

      setTickets(Array.isArray(e.tickets) ? e.tickets : []);
      setTicketForm((prev) => ({
        ...prev,
        communication: communicationFromSettings(settings),
      }));
      setLastSavedAt(new Date());
      setHasUnsavedChanges(false);
      setSaveStatus('saved');
      setSaveErrorMessage(null);
      setVersionConflict(false);

      return { version: v, event: e };
    } catch (err: any) {
      if (err?.response?.status === 401) {
        setLoadError('Unauthorized: Please ensure you are logged in and have permission to view this event.');
      } else {
        setLoadError(formatApiError(err));
      }
      throw err;
    }
  }, [eventId, applyDraftVersion]);

  const lastSavedJsonRef = useRef<string>('');

  useEffect(() => {
    if (loading) return;
    const currentSnapshot = JSON.stringify({
      basics,
      details,
      participation,
      media,
      rulesForm,
      advancedSettings,
      financeForm,
    });
    if (!lastSavedJsonRef.current) {
      lastSavedJsonRef.current = currentSnapshot;
      return;
    }
    if (currentSnapshot !== lastSavedJsonRef.current) {
      setHasUnsavedChanges(true);
      setSaveStatus((prev) => (prev === 'saved' ? 'idle' : prev));
    } else {
      setHasUnsavedChanges(false);
    }
  }, [
    loading,
    basics,
    details,
    participation,
    media,
    rulesForm,
    advancedSettings,
    financeForm,
  ]);

  useEffect(() => {
    if (storeDraftTitle !== basics.title) {
      setDraftTitle(basics.title);
    }
  }, [basics.title, setDraftTitle, storeDraftTitle]);

  useEffect(() => {
    if (!createMode || !hasUnsavedChanges) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [createMode, hasUnsavedChanges, localEventId]);

  useEffect(() => {
    const targetId = createMode && !localEventId ? '' : eventId;
    if (storeEventId !== targetId) {
      setEventId(targetId);
    }
  }, [eventId, setEventId, createMode, localEventId, storeEventId]);

  useEffect(() => {
    let alive = true;
    (async () => {
      setEventCategoriesLoading(true);
      setEventCategoriesError(null);
      try {
        const res = await eventDiscoveryApi.categories();
        if (alive) {
          setEventCategories(Array.isArray(res.data?.data) ? res.data.data : []);
        }
      } catch (err) {
        if (alive) setEventCategoriesError(formatApiError(err));
      } finally {
        if (alive) setEventCategoriesLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const refreshAllRef = useRef(refreshAll);
  useEffect(() => {
    refreshAllRef.current = refreshAll;
  }, [refreshAll]);

  useEffect(() => {
    if (authChecking || !isAuthenticated) return;

    if (createMode) {
      let alive = true;
      (async () => {
        if (!frameworksLoadedRef.current) {
          setLoading(true);
          setLoadError(null);
          try {
            const fwRes = await eventFrameworksApi.list();
            if (alive) {
              setFrameworks(Array.isArray(fwRes.data?.data) ? fwRes.data.data : []);
              frameworksLoadedRef.current = true;
            }
          } catch (err: any) {
            if (alive) setLoadError(formatApiError(err));
          } finally {
            if (alive) {
              setLoading(false);
              setStepRestored(true);
            }
          }
        }

        if (localEventId && alive) {
          setFieldsLoading(true);
          try {
            const gen = ++fieldsLoadGen.current;
            const [fr, formRes] = await Promise.all([
              registrationFieldsApi.list(localEventId),
              eventWorkflowApi.getEventRegistrationForm(localEventId).catch(() => null),
            ]);
            if (alive && gen === fieldsLoadGen.current) {
              setFields(
                mergeRegistrationFormFieldIds(
                  (fr.data?.data ?? []) as RegistrationFieldRecord[],
                  formRes?.data?.data?.fields ?? [],
                ),
              );
              setFieldsReady(true);
            }
          } catch {
            // Non-critical background fetch error
          } finally {
            if (alive) setFieldsLoading(false);
          }
        }
      })();
      return () => {
        alive = false;
      };
    }

    if (isLocalCreateRef.current) {
      // In-session creation already handled initial save and refresh — skip re-fetching to avoid race conditions.
      isLocalCreateRef.current = false;
      return;
    }

    setFieldsReady(false);
    if (!eventId || isStaticExportPlaceholder(eventId)) {
      if (!createMode) {
        setLoading(false);
        setLoadError('Event not found');
      }
      return;
    }

    let alive = true;
    (async () => {
      setLoading(true);
      setLoadError(null);
      setFieldsLoading(true);
      try {
        const gen = ++fieldsLoadGen.current;
        const [fwRes, , fr, formRes] = await Promise.all([
          eventFrameworksApi.list(),
          refreshAllRef.current(),
          registrationFieldsApi.list(eventId),
          eventWorkflowApi.getEventRegistrationForm(eventId).catch(() => null),
        ]);
        if (alive && gen === fieldsLoadGen.current) {
          setFrameworks(Array.isArray(fwRes.data?.data) ? fwRes.data.data : []);
          frameworksLoadedRef.current = true;
          setFields(
            mergeRegistrationFormFieldIds(
              (fr.data?.data ?? []) as RegistrationFieldRecord[],
              formRes?.data?.data?.fields ?? [],
            ),
          );
          setFieldsReady(true);
        }
      } catch {
        // Error already handled in refreshAll
      } finally {
        if (alive) {
          setLoading(false);
          setFieldsLoading(false);
        }
      }
    })();
    return () => {
      alive = false;
    };
  }, [eventId, createMode, localEventId, authChecking, isAuthenticated]);

  const coverImagePreviewUrl = useMemo(
    () => resolveStorageUrl(basics.cover_image) ?? (basics.cover_image.trim() || null),
    [basics.cover_image],
  );

  const logoPreviewUrl = useMemo(
    () => resolveStorageUrl(media.logo) ?? (media.logo.trim() || null),
    [media.logo],
  );

  const bannerPreviewUrl = useMemo(
    () => resolveStorageUrl(media.banner) ?? (media.banner.trim() || null),
    [media.banner],
  );

  const eventCategorySlugs = useMemo(
    () => eventCategories.map((category) => category.slug),
    [eventCategories],
  );

  const extractUploadedPath = (...candidates: Array<string | undefined>) => {
    const uploaded = candidates.find((value) => value && !value.startsWith('blob:'));
    if (!uploaded) {
      throw new Error('Upload succeeded but no image path was returned.');
    }
    return uploaded;
  };

  const handleCoverImageSelect = async (file: File | null) => {
    setCoverUploadError(null);
    if (!file) {
      setBasics((p) => ({ ...p, cover_image: '' }));
      setMedia((p) => ({ ...p, cover_image: '' }));
      setAdvancedSettings((p) => ({
        ...p,
        branding: { ...p.branding, cover_override: '' },
      }));
      setCoverUploadProgress(null);
      markDirty();
      return;
    }

    const validationError = validateEventCoverImage(file);
    if (validationError) {
      setCoverUploadError(validationError);
      return;
    }

    setCoverUploadProgress(0);
    try {
      const res = await eventWorkflowApi.uploadCoverImage(file, setCoverUploadProgress);
      const uploaded = extractUploadedPath(
        res.data?.data?.cover_image_path,
        res.data?.data?.cover_image_url,
        res.data?.data?.cover_image,
      );
      setBasics((p) => ({ ...p, cover_image: uploaded }));
      setMedia((p) => ({ ...p, cover_image: uploaded }));
      setAdvancedSettings((p) => ({
        ...p,
        branding: { ...p.branding, cover_override: uploaded },
      }));
      markDirty();
    } catch (err) {
      setCoverUploadError(formatApiError(err) || 'Failed to upload cover image.');
      setCoverUploadKey((k) => k + 1);
    } finally {
      setCoverUploadProgress(null);
    }
  };

  const handleLogoImageSelect = async (file: File | null) => {
    setLogoUploadError(null);
    if (!file) {
      setMedia((p) => ({ ...p, logo: '' }));
      setLogoUploadProgress(null);
      markDirty();
      return;
    }

    const validationError = validateOrgLogo(file);
    if (validationError) {
      setLogoUploadError(validationError);
      return;
    }

    setLogoUploadProgress(0);
    try {
      const res = await eventWorkflowApi.uploadLogoImage(file, setLogoUploadProgress);
      const uploaded = extractUploadedPath(
        res.data?.data?.logo_path,
        res.data?.data?.logo_url,
        res.data?.data?.logo,
      );
      setMedia((p) => ({ ...p, logo: uploaded }));
      markDirty();
    } catch (err) {
      setLogoUploadError(formatApiError(err) || 'Failed to upload logo.');
      setLogoUploadKey((k) => k + 1);
    } finally {
      setLogoUploadProgress(null);
    }
  };

  const handleBannerImageSelect = async (file: File | null) => {
    setBannerUploadError(null);
    if (!file) {
      setMedia((p) => ({ ...p, banner: '' }));
      setAdvancedSettings((p) => ({
        ...p,
        branding: { ...p.branding, banner_override: '' },
      }));
      setBannerUploadProgress(null);
      markDirty();
      return;
    }

    const validationError = validateOrgBanner(file);
    if (validationError) {
      setBannerUploadError(validationError);
      return;
    }

    setBannerUploadProgress(0);
    try {
      const res = await eventWorkflowApi.uploadBannerImage(file, setBannerUploadProgress);
      const uploaded = extractUploadedPath(
        res.data?.data?.banner_path,
        res.data?.data?.banner_url,
        res.data?.data?.banner,
      );
      setMedia((p) => ({ ...p, banner: uploaded }));
      setAdvancedSettings((p) => ({
        ...p,
        branding: { ...p.branding, banner_override: uploaded },
      }));
      markDirty();
    } catch (err) {
      setBannerUploadError(formatApiError(err) || 'Failed to upload banner.');
      setBannerUploadKey((k) => k + 1);
    } finally {
      setBannerUploadProgress(null);
    }
  };

  const ensureEventCreated = async (): Promise<string> => {
    const existingId =
      localEventId ||
      storeEventId ||
      (isValidEventWorkspaceId(eventId) ? eventId : null);
    if (existingId) return existingId;
    const currentBasics = basicsRef.current;
    const trimmedTitle = currentBasics.title.trim();
    if (!trimmedTitle) {
      throw new Error('Event title is required before saving.');
    }
    isLocalCreateRef.current = true;
    const res = await eventWorkflowApi.createDraft({
      title: trimmedTitle,
      type: isCustomOrganizing ? 'other' : currentBasics.type || 'other',
      mode: currentBasics.mode || 'online',
      ...(organizationId ? { organization_id: organizationId } : {}),
      ...((currentBasics.event_framework_id || customFramework?.id)
        ? { event_framework_id: currentBasics.event_framework_id || customFramework?.id }
        : {}),
    });
    const draft = res.data?.data;
    const id = draft?.event?.id as string | undefined;
    if (!id) throw new Error('No event id returned from draft creation.');
    applyDraftVersion(extractDraftVersion(res.data));
    if (draft?.event) {
      setEvent(draft.event as EventRecord);
    }
    setBasics((prev) => ({
      ...prev,
      title: prev.title || trimmedTitle,
    }));
    const targetStepId = WIZARD_STEPS[stepIndex]?.id ?? 'basics';
    rememberWorkspaceEventId(id);
    setLocalEventId(id);
    setEventId(id);
    router.replace(`/dashboard/events/${id}/edit?step=${targetStepId}`);
    return id;
  };

  const resolveWizardEventId = async (targetEventId?: string): Promise<string> => {
    const candidates = [
      targetEventId,
      localEventId,
      storeEventId,
      event?.id,
      isValidEventWorkspaceId(eventId) ? eventId : null,
    ].filter(Boolean) as string[];

    for (const candidate of candidates) {
      if (isValidEventWorkspaceId(candidate)) {
        return candidate;
      }
    }

    return ensureEventCreated();
  };

  const hasPaidTicketSetup = useMemo(
    () => eventHasPaidTicket(tickets) || Number(ticketForm.price) > 0,
    [tickets, ticketForm.price],
  );

  const user = useAuthStore((s) => s.user);
  const isOrgUser = useMemo(() => {
    const role = (user as { role?: string })?.role;
    return Boolean(
      (user as { organization_id?: string })?.organization_id ||
      role === 'organization_admin' ||
      role === 'organization_manager' ||
      pathname?.includes('/organization')
    );
  }, [user, pathname]);

  const redirectToOrganizerProfile = useCallback(() => {
    const returnTo = pathname
      ? `${pathname}${searchParams?.toString() ? `?${searchParams.toString()}` : ''}`
      : '/dashboard/events';
    if (isOrgUser) {
      router.push(`/dashboard/organization/finance?returnTo=${encodeURIComponent(returnTo)}`);
    } else {
      router.push(`/dashboard/organizer?setup=profile&returnTo=${encodeURIComponent(returnTo)}`);
    }
  }, [isOrgUser, pathname, router, searchParams]);

  const loadFinanceSettings = useCallback(async (id: string) => {
    const res = await eventsApi.getEventFinanceSettings(id);
    const data = res.data?.data;
    if (Array.isArray(data?.available_payment_methods)) {
      setPaymentMethodOptions(mapPaymentMethodCatalog(data.available_payment_methods));
    }
    setFinanceForm(financeFormFromApi(data));
    if (data?.payout_readiness) {
      setPayoutReadiness(data.payout_readiness);
    }
    setFinanceLoaded(true);
  }, []);

  const loadPayoutReadiness = useCallback(async (id: string) => {
    try {
      const res = await eventWorkflowApi.getPayoutReadiness(id);
      if (res.data?.success && res.data.data) {
        setPayoutReadiness(res.data.data);
        return res.data.data;
      }
    } catch {
      // Best-effort non-blocking check; review step publishCheck is canonical
    }
    return null;
  }, []);

  const saveFinance = async (targetEventId?: string) => {
    const id = targetEventId ?? eventId;
    if (!id || !hasPaidTicketSetup) return;

    const errors = validateFinanceForm(financeForm, false, { requireBankDetails: false });
    if (errors.payment_methods || errors.bank_account_number || errors.bank_ifsc) {
      setFinanceFieldErrors(errors);
      throw new Error(
        errors.payment_methods ||
          errors.bank_account_number ||
          errors.bank_ifsc ||
          'Fix finance settings before continuing.',
      );
    }
    setFinanceFieldErrors({});

    await eventsApi.updateEventFinanceSettings(id, buildEventFinancePayload(financeForm));
  };

  const persistAllSteps = async (targetEventId: string) => {
    await saveBasics(targetEventId);
    await saveDetails(targetEventId);
    await saveParticipation(targetEventId);
    if (hasPaidTicketSetup) {
      try {
        await saveFinance(targetEventId);
      } catch (err) {
        if (!isOrganizerProfileRequiredError(err)) {
          throw err;
        }
      }
    }
    await saveAdvanced(targetEventId);
    await refreshAll({ fresh: true, eventIdOverride: targetEventId });
  };

  const handleSaveDraft = async (currentStepOnly = false) => {
    if (savingRef.current) {
      logDraftVersion({ eventId, phase: 'skip', step: 'save-draft', inFlight: true });
      return;
    }
    savingRef.current = true;
    setSaving(true);
    setSaveStatus('saving');
    setStepError(null);
    setVersionConflict(false);
    try {
      const id = await resolveWizardEventId();
      if (!id) return;
      if (currentStepOnly) {
        if (stepIndex === 0) {
          await saveBasics(id);
        } else {
          await persistCurrentStep(id, { persistFinance: false, validate: false });
        }
      } else {
        await persistCurrentStep(id, { persistFinance: false, validate: false });
      }
      setHasUnsavedChanges(false);
      setSaveStatus('saved');
      setLastSavedAt(new Date());
      toast.success('Draft saved.');
    } catch (err) {
      const apiFieldErrors = apiValidationFieldErrors(err);
      if (Object.keys(apiFieldErrors).length > 0) {
        setFieldErrors(apiFieldErrors);
        focusFirstInvalidField(apiFieldErrors);
      }
      const message =
        err instanceof DraftVersionConflictError ? err.message : formatApiError(err);
      setStepError(message);
      setSaveStatus('failed');
      setSaveErrorMessage(message);
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  useEffect(() => {
    if (loading || !hasUnsavedChanges || savingRef.current) return;
    if (createMode && !localEventId) return;

    const timer = window.setTimeout(() => {
      void (async () => {
        if (savingRef.current) return;
        savingRef.current = true;
        setSaving(true);
        setSaveStatus('saving');
        setSaveErrorMessage(null);

        const savedBasics = { ...basicsRef.current };
        const savedDetails = { ...detailsRef.current };
        const savedParticipation = { ...participationRef.current };
        const savedMedia = { ...mediaRef.current };
        const savedRulesForm = { ...rulesFormRef.current };
        const savedAdvancedSettings = { ...advancedSettingsRef.current };
        const savedFinanceForm = { ...financeFormRef.current };

        try {
          const id = await resolveWizardEventId();
          if (!id) return;

          if (stepIndex === 0) {
            await saveBasics(id);
          } else if (stepIndex === 1) {
            const needsVenue = showVenueFields(basicsRef.current.mode);
            const hasLocation = needsVenue
              ? Boolean(detailsRef.current.venue_name?.trim() && detailsRef.current.venue_city?.trim())
              : true;
            await saveBasics(id);
            if (hasLocation && basicsRef.current.start_date && basicsRef.current.end_date) {
              await saveDetails(id);
            }
          } else if (stepIndex === 2) {
            await saveParticipation(id);
          } else if (stepIndex === 3) {
            // registration fields save inline
          } else if (stepIndex === 4) {
            await saveCommunicationSettings(id);
          } else if (stepIndex === 5) {
            await saveAdvanced(id);
          }

          lastSavedJsonRef.current = JSON.stringify({
            basics: savedBasics,
            details: savedDetails,
            participation: savedParticipation,
            media: savedMedia,
            rulesForm: savedRulesForm,
            advancedSettings: savedAdvancedSettings,
            financeForm: savedFinanceForm,
          });
          setHasUnsavedChanges(false);
          setSaveStatus('saved');
          setLastSavedAt(new Date());
          setSaveErrorMessage(null);
        } catch {
          // Gracefully maintain idle status if fields are partially complete or invalid
          lastSavedJsonRef.current = JSON.stringify({
            basics: savedBasics,
            details: savedDetails,
            participation: savedParticipation,
            media: savedMedia,
            rulesForm: savedRulesForm,
            advancedSettings: savedAdvancedSettings,
            financeForm: savedFinanceForm,
          });
          setHasUnsavedChanges(false);
          setSaveStatus('idle');
        } finally {
          savingRef.current = false;
          setSaving(false);
        }
      })();
    }, 1200);

    return () => window.clearTimeout(timer);
  }, [
    loading,
    hasUnsavedChanges,
    createMode,
    localEventId,
    basics,
    details,
    participation,
    media,
    rulesForm,
    advancedSettings,
    financeForm,
    stepIndex,
  ]);

  useEffect(() => {
    if (loading || stepRestored || !event) return;
    if (isWizardBlockedStatus(event.status)) {
      clearPersistedWizardStep(eventId);
      setStepRestored(true);
      return;
    }
    const stepParam = searchParams?.get('step');
    const fromUrl = stepParam ? WIZARD_STEPS.findIndex((s) => s.id === stepParam) : -1;
    if (fromUrl >= 0) {
      setStepIndex(fromUrl);
      persistWizardStep(eventId, fromUrl);
      setStepRestored(true);
      return;
    }
    const persisted = readPersistedWizardStep(eventId);
    if (persisted !== null) {
      setStepIndex(persisted);
    }
    setStepRestored(true);
  }, [loading, stepRestored, event, eventId, setStepIndex, searchParams]);

  useEffect(() => {
    if (!stepRestored || !eventId || !event) return;
    if (isWizardBlockedStatus(event.status)) {
      clearPersistedWizardStep(eventId);
      return;
    }
    persistWizardStep(eventId, stepIndex);
    if (typeof window !== 'undefined' && WIZARD_STEPS[stepIndex]) {
      // While transitioning from /new to /edit, let router.replace handle route navigation
      if (createMode && localEventId) return;

      const url = new URL(window.location.href);
      const targetStepId = WIZARD_STEPS[stepIndex].id;
      if (url.searchParams.get('step') !== targetStepId) {
        url.searchParams.set('step', targetStepId);
        window.history.replaceState(null, '', url.pathname + url.search);
      }
    }
  }, [stepIndex, stepRestored, eventId, event, createMode, localEventId]);

  const reloadFields = async () => {
    if (!eventId) return;
    const gen = ++fieldsLoadGen.current;
    setFieldsLoading(true);
    try {
      const [fr, formRes] = await Promise.all([
        registrationFieldsApi.list(eventId),
        eventWorkflowApi.getEventRegistrationForm(eventId).catch(() => null),
      ]);
      if (gen !== fieldsLoadGen.current) return;
      setFields(
        mergeRegistrationFormFieldIds(
          (fr.data?.data ?? []) as RegistrationFieldRecord[],
          formRes?.data?.data?.fields ?? [],
        ),
      );
      setFieldsReady(true);
    } finally {
      if (gen === fieldsLoadGen.current) {
        setFieldsLoading(false);
      }
    }
  };

  const mergeRegistrationField = useCallback((created: RegistrationFieldRecord) => {
    setFields((prev) => {
      if (prev.some((field) => field.id === created.id)) {
        return prev.map((field) => (field.id === created.id ? { ...field, ...created } : field));
      }
      return [...prev, created].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
    });
  }, []);

  useEffect(() => {
    if (fieldsLoading || !fieldsReady) return;
    let didChange = false;
    setTicketForm((prev) => {
      const nextFields = reconcileTicketFormFields(prev.form_fields, fields);
      if (
        nextFields.length === prev.form_fields.length &&
        nextFields.every((row, index) => row.form_field_id === prev.form_fields[index]?.form_field_id)
      ) {
        return prev;
      }
      didChange = true;
      return { ...prev, form_fields: nextFields };
    });
    if (didChange) {
      setTicketFieldErrors((prev) => {
        const next = { ...prev };
        let changed = false;
        Object.keys(next).forEach((key) => {
          if (key.startsWith('form_fields.')) {
            delete next[key];
            changed = true;
          }
        });
        return changed ? next : prev;
      });
    }
  }, [fields, fieldsLoading, fieldsReady]);

  const markSaved = useCallback(() => {
    setLastSavedAt(new Date());
    setHasUnsavedChanges(false);
    setSaveErrorMessage(null);
    setSaveStatus('saved');
  }, []);

  // Organizers can always choose individual or team registration manually.
  const teamAllowed = true;
  const authUser = useAuthStore((s) => s.user);

  const publishBlocked =
    stepIndex === WIZARD_STEPS.length - 1 &&
    (publishCheckLoading || publishCheck?.can_publish !== true);

  const markDirty = useCallback(() => {
    setHasUnsavedChanges(true);
    setSaveStatus((prev) => (prev === 'saved' ? 'idle' : prev));
  }, []);

  const handleModeChange = useCallback(
    (mode: string) => {
      setBasics((p) => ({ ...p, mode }));
      if (mode === 'online') {
        setDetails((p) => ({
          ...p,
          venue_name: '',
          venue_address: '',
          venue_city: '',
          venue_state: '',
          venue_country: '',
          venue_postal_code: '',
        }));
        setFieldErrors((prev) => {
          const next = { ...prev };
          delete next.venue_name;
          delete next.venue_city;
          return next;
        });
      }
      if (mode === 'offline') {
        setDetails((p) => ({ ...p, online_link: '' }));
        setFieldErrors((prev) => {
          const next = { ...prev };
          delete next.online_link;
          return next;
        });
      }
      markDirty();
    },
    [markDirty],
  );

  const footerMessage = useMemo(
    () =>
      wizardSaveStatusMessage(saveStatus, {
        hasUnsavedChanges,
        lastSavedAt,
        error: saveErrorMessage,
        formatSavedAt,
      }),
    [saveStatus, hasUnsavedChanges, lastSavedAt, saveErrorMessage],
  );

  const stickyValidationMessages = useMemo(() => {
    const messages = Object.values(fieldErrors).filter(Boolean);
    return messages;
  }, [fieldErrors]);

  const isDraft = createMode && !localEventId ? true : event?.status === 'draft';
  const tzHint = timezoneLabel(basics.timezone);

  const saveDraftStep = async (
    step: string,
    payload: Record<string, unknown>,
    targetEventId?: string,
  ): Promise<number> => {
    const id = targetEventId ?? eventId;
    if (!id) throw new Error('Event not created yet.');

    // Capture the step payload before any network/error handling so conflict
    // retries always re-submit the user's unsaved edits for this step.
    const pendingPayload = payload;

    return saveQueueRef.current.enqueue(async () => {
      // Read the ref at execution time (not render time) so sequential
      // basics → details → media always see the latest bumped version.
      const submittedVersion = versionRef.current;
      if (typeof submittedVersion !== 'number' || !Number.isFinite(submittedVersion)) {
        throw new Error('Draft version is not loaded yet.');
      }
      logDraftVersion({
        eventId: id,
        step,
        submittedVersion,
        phase: 'start',
        inFlight: true,
      });

      try {
        const { version: returnedVersion } = await saveDraftWithConflictRetry({
          submittedVersion,
          save: async (version) => {
            // `version` is either the live ref at queue start, or the
            // corrected token after a conflict refetch — always use it.
            const res = await eventWorkflowApi.saveStep(id, step, {
              version,
              payload: pendingPayload,
            });
            return res.data;
          },
          resolveLatestVersion: async () => {
            // Refetch locking token only — form fields stay intact.
            return syncDraftVersionFromServer(id);
          },
          onConflict: (latestVersion, attempt) => {
            logDraftVersion({
              eventId: id,
              step,
              submittedVersion: versionRef.current,
              returnedVersion: latestVersion,
              phase: attempt === 1 ? 'conflict-retry' : 'conflict',
              inFlight: true,
            });
          },
        });

        const applied = applyDraftVersion(returnedVersion);
        setVersionConflict(false);
        logDraftVersion({
          eventId: id,
          step,
          submittedVersion,
          returnedVersion: applied,
          phase: 'complete',
          inFlight: saveQueueRef.current.isInFlight,
        });
        return applied;
      } catch (err) {
        if (!isDraftVersionConflict(err)) {
          throw err;
        }

        // Automatic retry already failed — surface to the user.
        logDraftVersion({
          eventId: id,
          step,
          submittedVersion: versionRef.current,
          phase: 'conflict',
          inFlight: saveQueueRef.current.isInFlight,
        });

        let latestVersion: number | null = versionRef.current;
        try {
          latestVersion = await syncDraftVersionFromServer(id);
        } catch {
          // Keep local version if reload fails; still surface the conflict.
        }

        setVersionConflict(true);
        throw new DraftVersionConflictError(DRAFT_VERSION_CONFLICT_MESSAGE, latestVersion);
      }
    });
  };

  const saveBasics = async (targetEventId?: string) => {
    const currentBasics = basicsRef.current;
    const activeTitle = currentBasics.title.trim();
    const sd =
      fromDatetimeLocal(currentBasics.start_date) ??
      event?.start_date ??
      new Date(Date.now() + 86400000).toISOString();
    const ed =
      fromDatetimeLocal(currentBasics.end_date) ??
      event?.end_date ??
      new Date(Date.now() + 172800000).toISOString();

    const id = targetEventId ?? eventId ?? localEventId ?? undefined;
    let coverImage = currentBasics.cover_image.trim() || null;

    if (coverImageFile) {
      if (!id) {
        throw new Error('Save the draft before uploading a cover image.');
      }
      setCoverImageProgress(0);
      try {
        const res = await eventWorkflowApi.uploadCoverImage(id, coverImageFile, setCoverImageProgress);
        const uploaded = res.data?.data?.cover_image as string | undefined;
        if (uploaded) {
          coverImage = uploaded;
          setBasics((p) => ({ ...p, cover_image: uploaded }));
          setMedia((p) => ({ ...p, cover_image: uploaded }));
        }
        setCoverImageFile(null);
      } finally {
        setCoverImageProgress(null);
      }
    }

    const payload: Record<string, unknown> = {
      title: activeTitle || 'Untitled Event',
      description: currentBasics.description || null,
      short_description: currentBasics.short_description || null,
      event_framework_id: currentBasics.event_framework_id || customFramework?.id || null,
      mode: currentBasics.mode,
      start_date: sd,
      end_date: ed,
      timezone: currentBasics.timezone || DEFAULT_EVENT_TIMEZONE,
      visibility: currentBasics.visibility,
      contact_email: currentBasics.contact_email.trim() || null,
      contact_phone: currentBasics.contact_phone.trim() || null,
      cover_image: coverImage,

      custom_event_type: isCustomOrganizing ? currentBasics.custom_event_type.trim() : null,
      type: isCustomOrganizing ? 'other' : currentBasics.type,
      registration_type: teamAllowed ? currentBasics.registration_type : 'individual',
    };
    if (currentBasics.slug.trim() && !isAutoGeneratedPlaceholderSlug(currentBasics.slug.trim())) {
      payload.slug = currentBasics.slug.trim().toLowerCase();
    }
    const nv = await saveDraftStep('basics', payload, id);
    if (activeTitle) {
      setBasics((prev) => ({ ...prev, title: activeTitle }));
    }
    return nv;
  };

  const saveDetails = async (targetEventId?: string) => {
    if (participantLimitError) {
      toast.error(participantLimitError);
      throw new Error(participantLimitError);
    }
    const needsVenue = showVenueFields(basics.mode);
    const needsOnline = showOnlineLinkField(basics.mode);

    const payload: Record<string, unknown> = {
      venue_name: needsVenue ? details.venue_name || null : null,
      venue_address: needsVenue ? details.venue_address || null : null,
      venue_city: needsVenue ? details.venue_city || null : null,
      venue_state: needsVenue ? details.venue_state || null : null,
      venue_country: needsVenue ? details.venue_country || null : null,
      venue_postal_code: needsVenue ? details.venue_postal_code || null : null,

      max_participants: details.max_participants === '' ? null : Number(details.max_participants),
      registration_start: fromDatetimeLocal(details.registration_start),
      registration_end: fromDatetimeLocal(details.registration_end),

    };
    const attendanceJson = details.attendance_defaults_json.trim();
    if (attendanceJson) {
      try {
        const parsed = JSON.parse(attendanceJson);
        if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
          throw new Error('Attendance defaults must be a JSON object.');
        }
        payload.attendance_defaults = parsed;
      } catch (err) {
        throw new Error(err instanceof Error ? err.message : 'Attendance defaults must be valid JSON.');
      }
    }
    const id = targetEventId ?? eventId ?? localEventId ?? undefined;
    const nv = await saveDraftStep('details', payload, id);
    return nv;
  };

  const saveParticipation = async (targetEventId?: string) => {
    const id = targetEventId ?? eventId;
    if (!id) throw new Error('Event not created yet.');
    const errors = validateParticipationForm(participation, { teamAllowed });
    if (Object.keys(errors).length > 0) {
      setParticipationErrors(errors);
      throw new Error(firstParticipationError(errors) ?? 'Fix participation errors.');
    }
    setParticipationErrors({});

    let payload;
    try {
      payload = buildParticipationSettingsPayload(participation);
    } catch {
      throw new Error('Advanced configuration must be valid JSON.');
    }

    await eventWorkflowApi.updateSettings(id, {
      registration_type: teamAllowed ? payload.registration_type : 'individual',
      settings: payload.settings,
      ...(payload.submission_rules ? { submission_rules: payload.submission_rules } : {}),
    });
  };

  const saveAdvanced = async (targetEventId?: string) => {
    const id = targetEventId ?? eventId;
    if (!id) throw new Error('Event not created yet.');

    let coverImage = media.cover_image.trim() || null;
    let logo = media.logo.trim() || null;
    let banner = media.banner.trim() || null;

    const uploadBrandingFile = async (
      file: File | null,
      uploadFn: (eventId: string, f: File, onProgress?: (pct: number) => void) => Promise<{ data?: { data?: Record<string, string> } }>,
      field: 'cover_image' | 'logo' | 'banner',
      setProgress: (value: number | null) => void,
      clearFile: () => void,
    ) => {
      if (!file) return;
      setProgress(0);
      try {
        const res = await uploadFn(id, file, setProgress);
        const uploaded = res.data?.data?.[field];
        if (uploaded) {
          if (field === 'cover_image') coverImage = uploaded;
          if (field === 'logo') logo = uploaded;
          if (field === 'banner') banner = uploaded;
        }
        clearFile();
      } finally {
        setProgress(null);
      }
    };

    await uploadBrandingFile(
      brandingCoverFile,
      eventWorkflowApi.uploadCoverImage,
      'cover_image',
      setBrandingCoverProgress,
      () => setBrandingCoverFile(null),
    );
    await uploadBrandingFile(
      brandingLogoFile,
      eventWorkflowApi.uploadLogo,
      'logo',
      setBrandingLogoProgress,
      () => setBrandingLogoFile(null),
    );
    await uploadBrandingFile(
      brandingBannerFile,
      eventWorkflowApi.uploadBanner,
      'banner',
      setBrandingBannerProgress,
      () => setBrandingBannerFile(null),
    );

    if (coverImage || logo || banner) {
      setMedia((prev) => ({
        ...prev,
        cover_image: coverImage ?? prev.cover_image,
        logo: logo ?? prev.logo,
        banner: banner ?? prev.banner,
      }));
      if (coverImage) {
        setBasics((prev) => ({ ...prev, cover_image: coverImage! }));
        setAdvancedSettings((prev) => ({
          ...prev,
          branding: { ...prev.branding, cover_override: coverImage! },
        }));
      }
      if (banner) {
        setAdvancedSettings((prev) => ({
          ...prev,
          branding: { ...prev.branding, banner_override: banner! },
        }));
      }
    }

    await eventWorkflowApi.updateSettings(id, {
      settings: buildAdvancedSettingsPayload({ ...advancedSettings, rules: rulesForm }),
    });

    const payload: Record<string, unknown> = {
      cover_image: coverImage,
      logo: logo,
      banner: banner,
    };
    const nv = await saveDraftStep('media', payload, targetEventId);
    return nv;
  };

  const createDraftForRegistration = async (): Promise<string> => {
    const id = await ensureEventCreated();
    await saveBasics(id);
    await saveDetails(id);
    await saveParticipation(id);
    await refreshAll({ fresh: true, eventIdOverride: id });
    const gen = ++fieldsLoadGen.current;
    const [fr, formRes] = await Promise.all([
      registrationFieldsApi.list(id),
      eventWorkflowApi.getEventRegistrationForm(id).catch(() => null),
    ]);
    if (gen === fieldsLoadGen.current) {
      setFields(
        mergeRegistrationFormFieldIds(
          (fr.data?.data ?? []) as RegistrationFieldRecord[],
          formRes?.data?.data?.fields ?? [],
        ),
      );
      setFieldsReady(true);
    }
    markSaved();
    return id;
  };

  useEffect(() => {
    if (!createMode || localEventId || stepIndex !== 3) return;
    if (!basics.title.trim() || !basics.start_date || !basics.end_date) return;

    const lockKey = `${basics.title}|${basics.start_date}|${basics.end_date}`;
    if (registrationAutoDraftLocks.has(lockKey)) return;
    registrationAutoDraftLocks.add(lockKey);

    let alive = true;
    (async () => {
      setSaving(true);
      setSaveStatus('saving');
      setStepError(null);
      setSaveErrorMessage(null);
      try {
        await createDraftForRegistration();
      } catch (err) {
        registrationAutoDraftLocks.delete(lockKey);
        if (!alive) return;
        const message = formatApiError(err);
        setStepError(message);
        setSaveStatus('failed');
        setSaveErrorMessage(message);
      } finally {
        if (alive) {
          setSaving(false);
          setSaveStatus((prev) => (prev === 'saving' ? 'idle' : prev));
        }
      }
    })();

    return () => {
      alive = false;
    };
  }, [createMode, localEventId, stepIndex, basics.title, basics.start_date, basics.end_date]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await paymentMethodsApi.list();
        if (!alive) return;
        const mapped = mapPaymentMethodCatalog(res.data?.data);
        setPaymentMethodOptions(mapped);
        setFinanceForm((prev) => {
          const allowed = mapped.map((method) => method.id);
          const selected = prev.payment_methods.filter((method) => allowed.includes(method));
          return {
            ...prev,
            payment_methods: selected.length ? selected : allowed,
          };
        });
      } catch {
        // Keep fallback options.
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    if ((stepIndex !== 4 && stepIndex !== 6) || !eventId) return;
    if (!financeLoaded) {
      (async () => {
        try {
          await loadFinanceSettings(eventId);
        } catch (err) {
          if (isOrganizerProfileRequiredError(err)) {
            setFinanceProfileMissing(true);
          }
          setFinanceLoaded(true);
        }
      })();
    }
    void loadPayoutReadiness(eventId);
  }, [stepIndex, financeLoaded, eventId, loadFinanceSettings, loadPayoutReadiness]);

  const saveCommunicationSettings = async (targetEventId?: string) => {
    const id = await resolveWizardEventId(targetEventId);
    await eventWorkflowApi.updateSettings(id, {
      settings: buildCommunicationSettingsPayload(ticketForm.communication),
    });
    return id;
  };

  const addTicket = async () => {
    if (saving) return;
    const errors = validateTicketForm(
      ticketForm,
      {
        registration_start: details.registration_start,
        registration_end: details.registration_end,
      },
      {
        existingNames: tickets
          .filter((t) => String(t.id) !== String(editingTicketId))
          .map((t) => String(t.name ?? '')),
      },
    );
    if (Object.keys(errors).length > 0) {
      setTicketFieldErrors(errors);
      setStepError(firstValidationMessage(errors as Record<string, string>) ?? 'Fix ticket errors.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setSaving(true);
    setStepError(null);
    setTicketFieldErrors({});
    try {
      const id = await resolveWizardEventId();
      const isEdit = Boolean(editingTicketId);
      const allowed = new Set(fields.map((field) => connectableFormFieldId(field)).filter((value): value is string => Boolean(value)));
      const stale = ticketForm.form_fields.filter((row) => !allowed.has(row.form_field_id));
      if (stale.length > 0) {
        const staleErrors: Record<string, string> = {};
        ticketForm.form_fields.forEach((row, index) => {
          if (!allowed.has(row.form_field_id)) {
            staleErrors[`form_fields.${index}.form_field_id`] =
              'This registration field is no longer available in this event’s registration form. Please remove it and select a valid field.';
          }
        });
        setTicketFieldErrors(staleErrors);
        setStepError('Some registration fields need attention. Review the highlighted fields below.');
        window.setTimeout(() => focusFirstInvalidField(staleErrors), 50);
        setSaving(false);
        return;
      }
      const isPublishedEvent =
        event?.status === 'published' ||
        event?.status === 'live' ||
        event?.status === 'approved';

      let savedTicket: any = null;
      if (isEdit) {
        const originalTicket = tickets.find((t) => String(t.id) === String(editingTicketId)) || {};
        const patchPayload = buildTicketPatchPayload(ticketForm, originalTicket, isPublishedEvent);

        if (Object.keys(patchPayload).length === 0) {
          toast('No changes to save.', { icon: 'ℹ️' });
          setEditingTicketId(null);
          setTicketForm(emptyTicketForm());
          setSaving(false);
          return;
        }

        const res = await eventWorkflowApi.updateTicket(editingTicketId!, patchPayload);
        savedTicket = res.data?.data;
      } else {
        const res = await eventWorkflowApi.createTicket(id, buildTicketApiPayload(ticketForm));
        savedTicket = res.data?.data;
      }

      if (!savedTicket) {
        throw new Error('Ticket was not returned by server.');
      }

      setTickets((prev) =>
        isEdit
          ? prev.map((ticket) => (String(ticket.id) === String(editingTicketId) ? savedTicket : ticket))
          : [...prev, savedTicket],
      );

      setEditingTicketId(null);
      setTicketForm(emptyTicketForm());

      try {
        await saveCommunicationSettings(id);
      } catch (commErr) {
        // Non-blocking background settings sync failure
      }

      try {
        const refreshed = await refreshAll({ fresh: true, eventIdOverride: id });
        if (refreshed?.event?.tickets && Array.isArray(refreshed.event.tickets)) {
          setTickets(refreshed.event.tickets);
        } else {
          setTickets((prev) =>
            isEdit
              ? prev.map((ticket) => (String(ticket.id) === String(editingTicketId) ? savedTicket : ticket))
              : [...prev.filter((t) => String(t.id) !== String(savedTicket.id)), savedTicket],
          );
        }
        void loadPayoutReadiness(id);
      } catch {
        setTickets((prev) =>
          isEdit
            ? prev.map((ticket) => (String(ticket.id) === String(editingTicketId) ? savedTicket : ticket))
            : [...prev.filter((t) => String(t.id) !== String(savedTicket.id)), savedTicket],
        );
      }
      notifyTicketsChanged(id);
      markSaved();
      toast.success(isEdit ? 'Ticket updated.' : 'Ticket added.');
    } catch (err) {
      const apiFieldErrors = apiValidationFieldErrors(err);
      if (Object.keys(apiFieldErrors).length > 0) {
        setTicketFieldErrors(apiFieldErrors);
        const formFieldError = Object.entries(apiFieldErrors).find(([key]) => key.startsWith('form_fields.'))?.[1];
        setStepError(formFieldError ?? formatApiError(err));
        window.setTimeout(() => focusFirstInvalidField(apiFieldErrors), 50);
      } else {
        setStepError(formatApiError(err));
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } finally {
      setSaving(false);
    }
  };

  const startEditTicket = async (ticket: Record<string, any>) => {
    setEditingTicketId(String(ticket.id));
    setTicketFieldErrors({});
    setStepError(null);
    setTicketForm(ticketFormFromRecord(ticket, ticketForm.communication));
    try {
      const res = await eventWorkflowApi.getTicketFormFields(String(ticket.id));
      const data = res.data?.data;
      const mapped = (data?.form_fields ?? []).map((f, i) => ({
        form_field_id: String(f.form_field_id),
        required: Boolean(f.required),
        sort_order: Number(f.sort_order ?? i),
      }));
      setTicketForm((prev) => {
        const activityId = String(data?.event_activity_id ?? prev.event_activity_id ?? '');
        const activityIds = activityId
          ? Array.from(new Set([...(prev.activity_ids ?? []), activityId]))
          : (prev.activity_ids ?? []);
        return {
          ...prev,
          event_activity_id: activityId,
          activity_ids: activityIds,
          form_fields: mapped,
        };
      });
    } catch {
      // Keep ticketFormFromRecord values.
    }
  };

  const cancelEditTicket = () => {
    setEditingTicketId(null);
    setTicketFieldErrors({});
    setTicketForm((prev) => ({ ...emptyTicketForm(), communication: prev.communication }));
  };

  const deleteTicket = async (ticketId: string) => {
    if (!ticketId || deletingTicketId === ticketId) return;
    setDeletingTicketId(ticketId);
    setSaving(true);
    setStepError(null);
    try {
      const isUnsaved = ticketId.startsWith('temp') || ticketId.startsWith('mock') || ticketId.startsWith('new-');
      if (!isUnsaved) {
        try {
          await eventWorkflowApi.deleteTicket(ticketId);
        } catch (err: any) {
          if (err?.response?.status !== 404) {
            throw err;
          }
        }
      }
      if (editingTicketId === ticketId) {
        cancelEditTicket();
      }
      await refreshAll();
      const currentId = eventId || localEventId;
      if (currentId) notifyTicketsChanged(currentId);
    } catch (err) {
      setStepError(formatApiError(err));
    } finally {
      setDeletingTicketId(null);
      setSaving(false);
    }
  };

  const moveTicket = async (ticketId: string, direction: 'up' | 'down') => {
    const ids = tickets.map((t) => String(t.id));
    const index = ids.indexOf(ticketId);
    const target = direction === 'up' ? index - 1 : index + 1;
    if (index < 0 || target < 0 || target >= ids.length) return;
    [ids[index], ids[target]] = [ids[target], ids[index]];

    setSaving(true);
    setStepError(null);
    try {
      await eventWorkflowApi.reorderTickets(eventId, ids);
      await refreshAll({ fresh: true });
      if (eventId) notifyTicketsChanged(eventId);
    } catch (err) {
      setStepError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const loadPublishCheck = async (targetEventId = eventId): Promise<PublishCheckResult> => {
    if (!targetEventId) {
      const emptyCheck = { can_publish: false, errors: ['Event draft has not been created yet.'] };
      setPublishCheck(emptyCheck);
      return emptyCheck;
    }

    setPublishCheckLoading(true);
    try {
      const res = await eventWorkflowApi.publishCheck(targetEventId);
      const check = normalizePublishCheck(res);
      setPublishCheck(check);
      return check;
    } catch (err) {
      const check = publishCheckFromError(err) ?? {
        can_publish: false,
        errors: [formatApiError(err)],
      };
      setPublishCheck(check);
      return check;
    } finally {
      setPublishCheckLoading(false);
    }
  };

  const debouncedLoadPublishCheck = useDebouncedCallback(() => {
    loadPublishCheck().catch(() => setPublishCheckLoading(false));
  }, 400);

  useEffect(() => {
    if (stepIndex === 6 && eventId) {
      refreshAll({ fresh: true, eventIdOverride: eventId }).catch(() => {});
      debouncedLoadPublishCheck();
    }
  }, [stepIndex, eventId, debouncedLoadPublishCheck]);

  useEffect(() => {
    if (!eventId || loading || isStaticExportPlaceholder(eventId)) return;
    trackWizardStepViewed(eventId, stepIndex, WIZARD_STEPS[stepIndex].id);
  }, [stepIndex, eventId, loading]);

  useEffect(() => {
    return () => {
      if (!eventId || isStaticExportPlaceholder(eventId)) return;
      if (stepIndex < WIZARD_STEPS.length - 1) {
        trackWizardDraftAbandoned(eventId, stepIndex, WIZARD_STEPS[stepIndex].id);
      }
    };
  }, [eventId, stepIndex]);

  const finalizeAndPublish = async () => {
    if (savingRef.current) {
      logDraftVersion({ eventId, phase: 'skip', step: 'publish', inFlight: true });
      return;
    }
    savingRef.current = true;
    setSaving(true);
    setSaveStatus('saving');
    setStepError(null);
    setSaveErrorMessage(null);
    setVersionConflict(false);
    try {
      const validationError = validateCurrentStep();
      if (validationError) {
        setStepError(validationError);
        setSaveErrorMessage(validationError);
        setSaveStatus('failed');
        return;
      }
      const publishEventId = createMode && !localEventId ? await ensureEventCreated() : eventId;
      if (!publishEventId) {
        throw new Error('Event not created yet.');
      }

      // Always flush latest form values before publish / live update.
      await persistAllSteps(publishEventId);

      const liveStatus = String(event?.status ?? '').toLowerCase();
      if (liveStatus === 'published' || liveStatus === 'live') {
        clearPersistedWizardStep(publishEventId);
        toast.success('Changes updated on your public event page.');
        setPublishRedirecting(true);
        router.push(`/dashboard/events/${publishEventId}/overview`);
        return;
      }

      trackWizardEvent(WIZARD_ANALYTICS_EVENTS.PUBLISH_ATTEMPT, { event_id: publishEventId });
      await saveDraftStep('review', { confirmed: true }, publishEventId);

      const latestPublishCheck = await loadPublishCheck(publishEventId);
      if (!latestPublishCheck.can_publish) {
        const reasons = latestPublishCheck.errors.length
          ? latestPublishCheck.errors
          : ['Server publish validation did not pass.'];
        const message = reasons.slice(0, 3).join(' ');
        setStepError(message);
        setSaveStatus('failed');
        setSaveErrorMessage(message);
        trackWizardEvent(WIZARD_ANALYTICS_EVENTS.PUBLISH_FAILED, {
          event_id: publishEventId,
          reasons,
        });
        return;
      }

      const publishResponse = await eventWorkflowApi.publish(publishEventId);
      const publishedEvent = publishResponse.data?.data as EventRecord | undefined;
      if (publishedEvent) {
        setEvent(publishedEvent);
        setBasics((prev) => ({
          ...prev,
          slug: isAutoGeneratedPlaceholderSlug(publishedEvent.slug) ? '' : (publishedEvent.slug ?? prev.slug),
        }));
      }
      trackWizardEvent(WIZARD_ANALYTICS_EVENTS.PUBLISH_SUCCESS, { event_id: publishEventId });
      clearPersistedWizardStep(publishEventId);
      toast.success('Event published successfully!');
      // Keep a full-screen "redirecting" state visible until the destination
      // route loads (first compile in dev can take a long time).
      setPublishRedirecting(true);
      router.push(`/dashboard/events/${publishEventId}/overview`);
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 402) {
        trackWizardEvent(WIZARD_ANALYTICS_EVENTS.PUBLISH_FAILED, {
          event_id: eventId,
          reasons: ['subscription_required'],
        });
        router.push(`/dashboard/settings/subscription?returnTo=${encodeURIComponent(pathname ?? `/dashboard/events/${eventId}/edit`)}`);
        return;
      }
      const failedPublishCheck = publishCheckFromError(err);
      if (failedPublishCheck) {
        setPublishCheck(failedPublishCheck);
      }
      const message =
        err instanceof DraftVersionConflictError
          ? err.message
          : failedPublishCheck?.errors.length
            ? failedPublishCheck.errors.slice(0, 3).join(' ')
            : formatApiError(err);
      setStepError(message);
      setSaveStatus('failed');
      setSaveErrorMessage(message);
      trackWizardEvent(WIZARD_ANALYTICS_EVENTS.PUBLISH_FAILED, {
        event_id: eventId,
        reasons: [message],
      });
      await loadPublishCheck();
    } finally {
      savingRef.current = false;
      setSaving(false);
      setSaveStatus((prev) => (prev === 'saving' ? 'idle' : prev));
    }
  };

  const validateCurrentStep = (): string | null => {
    if (stepIndex === 0) {
      if (!basics.title.trim()) {
        return 'Event title is required.';
      }
      if (!basics.mode) {
        return 'Event mode is required.';
      }
      const basicsErrors = validateBasicsStep({
        title: basics.title,
        timezone: basics.timezone,
        slug: basics.slug,
      });
      const step0Errors = { ...basicsErrors };
      if (Object.keys(step0Errors).length > 0) {
        setFieldErrors(step0Errors as Record<string, string>);
        focusFirstInvalidField(step0Errors as Record<string, string>);
        return firstValidationMessage(step0Errors as Record<string, string>) ?? 'Fix basics errors.';
      }
    } else if (stepIndex === 1) {
      const startIso = fromDatetimeLocal(basics.start_date);
      const endIso = fromDatetimeLocal(basics.end_date);
      if (!basics.start_date?.trim() || !basics.end_date?.trim() || !startIso || !endIso) {
        return 'Event start and end are required.';
      }
      const dateErrors = validateDetailsStep({
        mode: basics.mode,
        start_date: basics.start_date,
        end_date: basics.end_date,
        registration_start: details.registration_start,
        registration_end: details.registration_end,
        online_link: details.online_link,
        venue_name: details.venue_name,
        venue_city: details.venue_city,
        venue_postal_code: details.venue_postal_code,
      });
      if (Object.keys(dateErrors).length > 0) {
        setFieldErrors(dateErrors as Record<string, string>);
        focusFirstInvalidField(dateErrors as Record<string, string>);
        return firstValidationMessage(dateErrors as Record<string, string>) ?? 'Fix date errors.';
      }
    } else if (stepIndex === 2) {
      const errors = validateParticipationForm(participation, { teamAllowed });
      if (Object.keys(errors).length > 0) {
        setParticipationErrors(errors);
        return firstParticipationError(errors) ?? 'Fix participation errors.';
      }
      setParticipationErrors({});
    } else if (stepIndex === 4) {
      if (hasPaidTicketSetup) {
        const errors = validateFinanceForm(financeForm, false, { requireBankDetails: false });
        if (errors.payment_methods) {
          setFinanceFieldErrors(errors);
          return errors.payment_methods;
        }
        setFinanceFieldErrors({});
      }
    }
    return null;
  };

  const showStepFailure = (message: string) => {
    setStepError(message);
    setSaveErrorMessage(message);
    setSaveStatus('failed');
    toast.error(message);
    try {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch {}
  };

  const isPublished =
    event?.status === 'published' ||
    event?.status === 'live' ||
    event?.status === 'approved';
  const isDraftEvent = !event || event.status === 'draft' || createMode;
  /** Existing-event edits from overview cards — save this section only, don't force later steps.
   *  Only active when a specific ?step= param is present (clicked from a section card)
   *  or when explicitly requested via ?mode=section.
   *  When editing via "Edit Setup" (no ?step=), behave as a full multi-step wizard. */
  const isSectionEditMode = searchParams?.get('mode') === 'section';
  const wizardTitle = isPublished ? 'Edit event' : 'Create event';

  const persistCurrentStep = async (
    targetEventId?: string,
    options?: { persistFinance?: boolean; validate?: boolean },
  ) => {
    const persistFinance = options?.persistFinance !== false;
    const shouldValidate = options?.validate !== false;
    const id = targetEventId ?? (await resolveWizardEventId());
    if (shouldValidate) {
      const validationError = validateCurrentStep();
      if (validationError) {
        throw new Error(validationError);
      }
    }

    if (stepIndex === 0) {
      await saveBasics(id);
    } else if (stepIndex === 1) {
      await saveBasics(id);
      await saveDetails(id);
    } else if (stepIndex === 2) {
      await saveParticipation(id);
    } else if (stepIndex === 3) {
      /* registration fields save inline in the builder */
    } else if (stepIndex === 4) {
      await saveCommunicationSettings(id);
      if (hasPaidTicketSetup && persistFinance) {
        try {
          await saveFinance(id);
        } catch (err) {
          if (isOrganizerProfileRequiredError(err)) {
            setFinanceProfileMissing(true);
          } else {
            throw err;
          }
        }
      }
    } else if (stepIndex === 5) {
      await saveAdvanced(id);
    }

    if (id) {
      await refreshAll({ fresh: true, eventIdOverride: id });
      trackWizardStepCompleted(id, stepIndex, WIZARD_STEPS[stepIndex].id);
    }
    markSaved();
    return id;
  };

  const saveSectionAndExit = async () => {
    if (coverUploadProgress !== null) {
      showStepFailure('Please wait for the featured image upload to finish.');
      return;
    }
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    setSaveStatus('saving');
    setStepError(null);
    setFieldErrors({});
    setSaveErrorMessage(null);
    setVersionConflict(false);
    try {
      const targetId = await persistCurrentStep(undefined, { persistFinance: false, validate: false });
      const live =
        event?.status === 'published' ||
        event?.status === 'live' ||
        event?.status === 'approved';
      toast.success(live ? 'Changes updated on your public event page.' : 'Section saved.');
      const destId = targetId || eventId;
      if (destId) {
        router.push(`/dashboard/events/${destId}/overview`);
      } else {
        router.push('/dashboard/events');
      }
    } catch (err) {
      const apiFieldErrors = apiValidationFieldErrors(err);
      if (Object.keys(apiFieldErrors).length > 0) {
        const financeKeys = [
          'payment_methods',
          'bank_account_number',
          'bank_ifsc',
          'bank_account_name',
          'bank_name',
          'bank_address',
          'bank_account_type',
        ];
        const financeOnly: Record<string, string> = {};
        const other: Record<string, string> = {};
        for (const [key, value] of Object.entries(apiFieldErrors)) {
          if (financeKeys.includes(key)) financeOnly[key] = value;
          else other[key] = value;
        }
        if (Object.keys(financeOnly).length > 0) setFinanceFieldErrors(financeOnly);
        if (Object.keys(other).length > 0) {
          setFieldErrors(other);
          focusFirstInvalidField(other);
        }
      }
      showStepFailure(formatApiError(err));
    } finally {
      savingRef.current = false;
      setSaving(false);
      setSaveStatus((prev) => (prev === 'saving' ? 'idle' : prev));
    }
  };

  const goNext = async () => {
    if (coverUploadProgress !== null) {
      showStepFailure('Please wait for the featured image upload to finish.');
      return;
    }
    if (savingRef.current) {
      logDraftVersion({
        eventId,
        step: WIZARD_STEPS[stepIndex]?.id,
        phase: 'skip',
        inFlight: true,
      });
      return;
    }
    savingRef.current = true;
    setSaving(true);
    setSaveStatus('saving');
    setStepError(null);
    setFieldErrors({});
    setSaveErrorMessage(null);
    setVersionConflict(false);
    try {
      const validationError = validateCurrentStep();
      if (validationError) {
        showStepFailure(validationError);
        return;
      }

      const targetId = await persistCurrentStep();

      if (isSectionEditMode) {
        const destId = targetId || eventId || localEventId;
        const live =
          event?.status === 'published' ||
          event?.status === 'live' ||
          event?.status === 'approved';
        toast.success(live ? 'Changes updated on your public event page.' : 'Section saved successfully.');
        if (destId) {
          router.push(`/dashboard/events/${destId}/overview`);
        } else {
          router.push('/dashboard/events');
        }
        return;
      }

      setHighestCompletedStep((prev) => Math.max(prev, stepIndex + 1));
      if (stepIndex < WIZARD_STEPS.length - 1) {
        setStepIndex(stepIndex + 1);
        persistWizardStep(targetId, stepIndex + 1);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (err) {
      if (isOrganizerProfileRequiredError(err)) {
        setFinanceProfileMissing(true);
        if (stepIndex === 4) {
          setHighestCompletedStep((prev) => Math.max(prev, stepIndex + 1));
          if (stepIndex < WIZARD_STEPS.length - 1) {
            setStepIndex(stepIndex + 1);
            persistWizardStep(eventId, stepIndex + 1);
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }
          return;
        }
        showStepFailure('Organizer profile setup is required for payout settings. You can complete your organizer profile later.');
        return;
      }
      const apiFieldErrors = apiValidationFieldErrors(err);
      if (Object.keys(apiFieldErrors).length > 0) {
        const financeKeys = [
          'payment_methods',
          'bank_account_number',
          'bank_ifsc',
          'bank_account_name',
          'bank_name',
          'bank_address',
          'bank_account_type',
        ];
        const financeOnly: Record<string, string> = {};
        const other: Record<string, string> = {};
        for (const [key, value] of Object.entries(apiFieldErrors)) {
          if (financeKeys.includes(key)) financeOnly[key] = value;
          else other[key] = value;
        }
        if (Object.keys(financeOnly).length > 0) {
          setFinanceFieldErrors(financeOnly);
        }
        if (Object.keys(other).length > 0) {
          setFieldErrors(other);
          focusFirstInvalidField(other);
        }
      }
      const message =
        err instanceof DraftVersionConflictError ? err.message : formatApiError(err);
      showStepFailure(message);
      trackWizardEvent(WIZARD_ANALYTICS_EVENTS.SAVE_FAILED, {
        event_id: eventId,
        step_id: WIZARD_STEPS[stepIndex].id,
        reasons: [message],
      });
    } finally {
      savingRef.current = false;
      setSaving(false);
      setSaveStatus((prev) => (prev === 'saving' ? 'idle' : prev));
    }
  };

  const goBack = () => {
    // Never trap the organizer on a step because a save/overlay is mid-flight.
    savingRef.current = false;
    setSaving(false);
    setSaveStatus((prev) => (prev === 'saving' ? 'idle' : prev));
    finishNavigationLoading();
    setStepError(null);
    setFieldErrors({});
    if (stepIndex > 0) setStepIndex(stepIndex - 1);
  };

  if (loading) {
    return (
      <div className="flex min-h-[320px] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="mx-auto max-w-lg rounded-xl border border-red-500/20 bg-bg p-8 shadow-xl">
        <div className="flex flex-col items-center space-y-4 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-50">
            <AlertCircle className="h-6 w-6 text-red-500" />
          </div>
          <div className="space-y-1">
            <h3 className="text-lg font-bold text-soot">
              {loadError.toLowerCase().includes('unauthorized') ? 'Access Denied' : 'Could not load event'}
            </h3>
            <p className="text-sm leading-relaxed text-moss">{loadError}</p>
          </div>
          <Link
            href="/dashboard/events"
            className="mt-4 rounded-lg bg-soot px-6 py-2 text-sm font-bold text-text-inverse transition-colors hover:bg-black"
          >
            Back to My Events
          </Link>
        </div>
      </div>
    );
  }

  if (!event && (!createMode || localEventId)) return null;

  if (publishRedirecting) {
    return (
      <div className="mx-auto flex min-h-[60vh] max-w-lg flex-col items-center justify-center gap-4 text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50">
          <Loader2 className="h-7 w-7 animate-spin text-emerald-600" />
        </div>
        <div className="space-y-1">
          <h3 className="text-lg font-bold text-soot">Event published!</h3>
          <p className="text-sm text-moss">Taking you to your event overview…</p>
        </div>
        <Link
          href={`/dashboard/events/${eventId}/overview`}
          className="mt-2 rounded-lg bg-soot px-6 py-2 text-sm font-bold text-text-inverse transition-colors hover:bg-black"
        >
          Open event overview
        </Link>
      </div>
    );
  }

  if (event && isWizardBlockedStatus(event.status)) {
    return (
      <div className="mx-auto max-w-lg rounded-lg border border-amber-500/30 bg-amber-500/5 p-6 text-sm">
        <p className="font-medium text-amber-800">This event cannot be edited in the creation wizard.</p>
        <Link
          href={`/dashboard/events/${eventId}/overview`}
          className="mt-4 inline-block text-primary underline"
        >
          Open event overview
        </Link>
      </div>
    );
  }

  return (
    <div className="ew-page">
      <WizardPageHeader
        title={wizardTitle}
        stepIndex={stepIndex}
        stepTotal={WIZARD_STEPS.length}
        stepLabels={WIZARD_STEPS.map((s) => s.title)}
        action={
          eventId ? (
            <Link href={`/dashboard/events/${eventId}/overview`} className="ew-exit-link">
              Exit to overview
            </Link>
          ) : (
            <Link href="/dashboard/organization/events" className="ew-exit-link">
              Exit to events
            </Link>
          )
        }
      />

      <div className="ew-step-pills">
        {WIZARD_STEPS.map((s, i) => (
          <button
            key={s.id}
            type="button"
            onClick={() => {
              // Allow free step navigation even if a save is hanging.
              savingRef.current = false;
              setSaving(false);
              if (createMode && !localEventId && i > highestCompletedStep) {
                setStepError('Complete the current step before moving ahead.');
                return;
              }
              setStepError(null);
              setFieldErrors({});
              setStepIndex(i);
            }}
            disabled={createMode && !localEventId && i > highestCompletedStep}
            className={`ew-step-pill${i === stepIndex ? ' ew-step-pill--active' : ''}`}
          >
            {i + 1}. {s.title}
          </button>
        ))}
      </div>

      {stepError ? (
        <div className="ew-alert">
          <AlertCircle className="h-4 w-4" />
          <div className="space-y-2">
            <span>{stepError}</span>
            {versionConflict && (
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  className="rounded-md border border-red-300 bg-bg px-3 py-1.5 text-xs font-medium text-red-800 hover:bg-red-50 disabled:opacity-50"
                  disabled={saving}
                  onClick={() => {
                    void (async () => {
                      if (!eventId || savingRef.current) return;
                      try {
                        const latest = await syncDraftVersionFromServer(eventId);
                        if (latest === null) {
                          throw new Error('Could not load the latest draft version.');
                        }
                        setVersionConflict(false);
                        setStepError(
                          `Synced to draft version ${latest}. Review your form values, then save again.`,
                        );
                      } catch (err) {
                        setStepError(formatApiError(err));
                      }
                    })();
                  }}
                >
                  Sync latest version (keep form)
                </button>
                <button
                  type="button"
                  className="rounded-md border border-red-300 bg-bg px-3 py-1.5 text-xs font-medium text-red-800 hover:bg-red-50 disabled:opacity-50"
                  disabled={saving}
                  onClick={() => {
                    void (async () => {
                      try {
                        await refreshAll({ fresh: true });
                        setStepError(null);
                        setVersionConflict(false);
                        toast.success('Loaded the latest draft from the server.');
                      } catch (err) {
                        setStepError(formatApiError(err));
                      }
                    })();
                  }}
                >
                  Reload draft from server
                </button>
              </div>
            )}
          </div>
        </div>
      ) : null}

      {stickyValidationMessages.length > 0 && !stepError ? (
        <div className="ew-alert ew-alert--sticky">
          <AlertCircle className="h-4 w-4" />
          <ul className="space-y-1">
            {stickyValidationMessages.map((msg) => (
              <li key={msg}>{msg}</li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="ew-body">
        <div className="ew-main">
      <div className="flex flex-col gap-4">
        {stepIndex === 0 && (
          <WizardPanelCard>
            <WizardPanelBody>
              {stepIndex === 0 && !basics.title.trim() ? (
                <WizardHelpBanner title="Quick tip">
                  Start with a clear event title — you can always refine the description later.
                </WizardHelpBanner>
              ) : null}

              <WizardFormSection
                title="Event identity"
                description="Title, tagline, and description shown on your public event page."
              >
                <div className="ew-field-grid ew-field-grid--2">
                  <Field label="Event title" required hint="Use a name people will recognize in search and listings.">
                    <input
                      className={inputClass}
                      value={basics.title}
                      onChange={(e) => setBasics((p) => ({ ...p, title: e.target.value }))}
                      placeholder="e.g. Annual Tech Summit 2026"
                      autoFocus
                    />
                  </Field>
                  <Field label="Tagline" hint="A short one-line summary shown in listings.">
                    <input
                      className={inputClass}
                      value={basics.short_description}
                      onChange={(e) => setBasics((p) => ({ ...p, short_description: e.target.value }))}
                      placeholder="e.g. Two days of talks, workshops, and networking"
                    />
                  </Field>
                </div>
                <Field label="Description" hint="Share what attendees can expect — agenda highlights, speakers, or goals.">
                  <textarea
                    className={textareaClass}
                    value={basics.description}
                    onChange={(e) => setBasics((p) => ({ ...p, description: e.target.value }))}
                    placeholder="Tell attendees about your event, who it's for, and why they should join."
                  />
                </Field>
              </WizardFormSection>

              <WizardFormSection
                title="Contact & listing"
                description="Organizer contact details and public URL slug. Cover image is set under Advanced → Branding."
                divider
              >
                <div className="grid gap-6 lg:grid-cols-2">
                  <Field label="Organizer contact email" hint="Defaults to your profile email.">
                    <input
                      type="email"
                      className={inputClass}
                      value={basics.contact_email}
                      onChange={(e) => setBasics((p) => ({ ...p, contact_email: e.target.value }))}
                      placeholder="contact@example.com"
                    />
                  </Field>
                  <Field label="Organizer contact phone">
                    <input
                      type="tel"
                      className={inputClass}
                      value={basics.contact_phone}
                      onChange={(e) => setBasics((p) => ({ ...p, contact_phone: e.target.value }))}
                      placeholder="+91..."
                    />
                  </Field>
                </div>

                <Field
                  label="Custom URL slug"
                  error={fieldErrors.slug}
                  hint={
                    isDraft
                      ? 'Optional. Lowercase letters, numbers, and hyphens only.'
                      : 'Slug can only be edited while the event is a draft.'
                  }
                >
                  <input
                    className={inputClass}
                    value={basics.slug}
                    onChange={(e) => setBasics((p) => ({ ...p, slug: e.target.value }))}
                    disabled={!isDraft}
                    placeholder="my-event-name"
                  />
                </Field>
              </WizardFormSection>
            </WizardPanelBody>
          </WizardPanelCard>
        )}

        {stepIndex === 1 && (
          <WizardPanelCard>
            <WizardPanelHeader
              embedded
              title="Dates & location"
              description="Set when your event runs, when registration opens, and where attendees join."
            />

            <WizardPanelBody>
              <WizardFormSection
                title="Event timing"
                description={`Start and end date/time for the live event (${tzHint}).`}
              >
                <div className="grid gap-6 sm:grid-cols-2">
                  <Field label={`Event start (${tzHint})`} required error={fieldErrors.start_date}>
                    <DateTimePicker
                      data-field="start_date"
                      min={nowDatetimeLocalValue()}
                      value={basics.start_date}
                      onChange={(val) => setBasics((p) => ({ ...p, start_date: val }))}
                      error={fieldErrors.start_date}
                    />
                  </Field>
                  <Field label={`Event end (${tzHint})`} required error={fieldErrors.end_date}>
                    <DateTimePicker
                      data-field="end_date"
                      min={basics.start_date || nowDatetimeLocalValue()}
                      value={basics.end_date}
                      onChange={(val) => setBasics((p) => ({ ...p, end_date: val }))}
                      error={fieldErrors.end_date}
                    />
                  </Field>
                </div>
              </WizardFormSection>

              <WizardFormSection
                title="Registration window"
                description={`Registration must open before it closes, and close before the event starts (${tzHint}).`}
                divider
              >
                <div className="grid gap-6 sm:grid-cols-2">
                  <Field label={`Registration opens (${tzHint})`} error={fieldErrors.registration_start}>
                    <DateTimePicker
                      value={details.registration_start}
                      onChange={(val) => setDetails((p) => ({ ...p, registration_start: val }))}
                      error={fieldErrors.registration_start}
                    />
                  </Field>
                  <Field label={`Registration closes (${tzHint})`} error={fieldErrors.registration_end}>
                    <DateTimePicker
                      value={details.registration_end}
                      onChange={(val) => setDetails((p) => ({ ...p, registration_end: val }))}
                      error={fieldErrors.registration_end}
                    />
                  </Field>
                </div>
              </WizardFormSection>

              <WizardFormSection
                title="Registration capacity"
                description="Maximum capacity cap for attendees at this event."
                divider
              >
                <Field
                  label="Maximum capacity cap"
                  hint={participantLimitError ? undefined : "Leave empty for unlimited registrations."}
                  error={participantLimitError || undefined}
                >
                  <input
                    type="number"
                    min={1}
                    className={`${inputClass} ${participantLimitError ? '!border-red-500 !bg-red-50 focus:!border-red-500 focus:!ring-red-200' : ''}`}
                    value={details.max_participants}
                    onChange={(e) => {
                      markDirty();
                      setDetails((p) => ({ ...p, max_participants: e.target.value }));
                    }}
                    placeholder="e.g. 500"
                  />
                </Field>
              </WizardFormSection>

              <WizardFormSection
                title="Location & venue"
                description="Event mode and venue or online meeting details."
                divider
              >
                <div className="grid gap-6">
                  <Field label="Event mode" required hint="Online, in-person, or hybrid. Venue fields appear for offline and hybrid events.">
                    <select
                      className={inputClass}
                      data-field="mode"
                      value={basics.mode}
                      onChange={(e) => handleModeChange(e.target.value)}
                    >
                      <option value="online">Online</option>
                      <option value="offline">Offline</option>
                      <option value="hybrid">Hybrid</option>
                    </select>
                  </Field>
                </div>

                {showVenueFields(basics.mode) && (
                  <div className="space-y-6 border-t border-nature-border/70 pt-6">
                    <SectionHeader title="Venue" description="Physical location details for in-person attendees." />
                    <Field label="Venue name" required error={fieldErrors.venue_name}>
                      <input
                        className={inputClass}
                        data-field="venue_name"
                        value={details.venue_name}
                        onChange={(e) => setDetails((p) => ({ ...p, venue_name: e.target.value }))}
                      />
                    </Field>
                    <Field label="Address line">
                      <textarea
                        className={textareaClass}
                        data-field="venue_address"
                        value={details.venue_address}
                        onChange={(e) => setDetails((p) => ({ ...p, venue_address: e.target.value }))}
                      />
                    </Field>
                    <VenueLocationSelects
                      values={{
                        venue_country: details.venue_country,
                        venue_state: details.venue_state,
                        venue_city: details.venue_city,
                        venue_postal_code: details.venue_postal_code,
                      }}
                      inputClass={inputClass}
                      cityError={fieldErrors.venue_city}
                      postalError={fieldErrors.venue_postal_code}
                      onChange={(patch) => setDetails((p) => ({ ...p, ...patch }))}
                    />
                  </div>
                )}
              </WizardFormSection>
            </WizardPanelBody>
          </WizardPanelCard>
        )}

        {stepIndex === 2 && (
          <ParticipationRulesPanel
            form={participation}
            onChange={(next) => {
              markDirty();
              setParticipation(next);
              setBasics((p) =>
                p.registration_type === next.registration_type
                  ? p
                  : { ...p, registration_type: next.registration_type }
              );
            }}
            teamAllowed={teamAllowed}
            fieldErrors={participationErrors}
          />
        )}

        {stepIndex === 3 && (
          <RegistrationBuilderPanel
            eventId={eventId}
            fields={fields}
            fieldsLoading={fieldsLoading}
            saving={saving}
            onReload={reloadFields}
            onFieldCreated={mergeRegistrationField}
            onError={setStepError}
            onSaved={markSaved}
          />
        )}

        {stepIndex === 4 && (
          <div className="space-y-8">
            <TicketsWizardPanel
              eventId={eventId}
              tickets={tickets}
              registrationFields={fields}
              ticketForm={ticketForm}
              onTicketFormChange={(next) => {
                setTicketForm(next);
                setTicketFieldErrors((prev) => {
                  if (next.form_fields === ticketForm.form_fields) return prev;
                  const copy = { ...prev };
                  let changed = false;
                  Object.keys(copy).forEach((key) => {
                    if (key.startsWith('form_fields.')) {
                      delete copy[key];
                      changed = true;
                    }
                  });
                  return changed ? copy : prev;
                });
                if (JSON.stringify(next.communication) !== JSON.stringify(ticketForm.communication)) {
                  markDirty();
                }
              }}
              onAddTicket={addTicket}
              onDeleteTicket={deleteTicket}
              onEditTicket={startEditTicket}
              onMoveTicket={moveTicket}
              onCancelEdit={cancelEditTicket}
              editingTicketId={editingTicketId}
              fieldErrors={ticketFieldErrors}
              saving={saving}
              isPublished={isPublished}
            />
            {!hasPaidTicketSetup ? (
              <div className="rounded-2xl border border-nature-border bg-plaster/35 px-5 py-4 text-sm text-moss">
                <p className="font-semibold text-soot">Finance &amp; payouts</p>
                <p className="mt-1">
                  No payout account is required for free events. Bank and payout details can be configured later.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="rounded-2xl border border-nature-border bg-plaster/35 px-5 py-4 text-sm text-moss">
                  <p className="font-semibold text-soot">Finance &amp; payouts</p>
                  <p className="mt-1">
                    Bank and payout details can be configured later. Missing payout details will not prevent you from saving this event as a draft.
                  </p>
                </div>
                {payoutReadiness?.payout_ready ? (
                  <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-5 py-4 text-sm text-emerald-950">
                    <p className="font-semibold">
                      {payoutReadiness.destination_type === 'temporary_route'
                        ? 'Temporary payout account configured for this event.'
                        : 'Payout account configured for this event.'}
                    </p>
                    {payoutReadiness.account_id_masked && (
                      <p className="mt-1 text-xs text-emerald-800">
                        Destination: {payoutReadiness.account_id_masked}
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4 text-sm text-amber-950">
                    <p className="font-semibold">Paid ticket sales require a payout account before activation.</p>
                    <p className="mt-1">
                      You can continue saving this event as a draft and configure payouts later.
                    </p>
                    {financeProfileMissing && (
                      <button
                        type="button"
                        onClick={redirectToOrganizerProfile}
                        className="mt-3 text-sm font-semibold text-amber-900 underline underline-offset-2"
                      >
                        {isOrgUser ? 'Go to organization finance settings' : 'Go to organizer profile'}
                      </button>
                    )}
                  </div>
                )}
                {!eventId ? (
                  <p className="rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4 text-sm text-amber-900">
                    Save the registration fields step first — finance settings become available once the event
                    draft is created.
                  </p>
                ) : !financeLoaded ? (
                  <div className="flex items-center justify-center rounded-2xl border border-nature-border bg-bg py-12">
                    <Loader2 className="h-7 w-7 animate-spin text-[#697C70]" />
                  </div>
                ) : (
                  <EventWizardFinanceFields
                    form={financeForm}
                    onChange={(next) => {
                      setFinanceForm(next);
                      setHasUnsavedChanges(true);
                    }}
                    fieldErrors={financeFieldErrors}
                    paymentMethodOptions={paymentMethodOptions}
                  />
                )}
              </div>
            )}
          </div>
        )}

        {stepIndex === 5 && (
          <AdvancedSettingsPanel
              form={advancedSettings}
              onChange={setAdvancedSettings}
              rulesForm={rulesForm}
              onRulesChange={setRulesForm}
              media={media}
              onDirty={markDirty}
              coverUpload={{
                previewUrl: coverImagePreviewUrl,
                uploadProgress: coverUploadProgress,
                uploadError: coverUploadError,
                uploadKey: coverUploadKey,
                disabled: saving || coverUploadProgress !== null,
                onFileSelect: (file) => {
                  void handleCoverImageSelect(file);
                },
              }}
              logoUpload={{
                previewUrl: logoPreviewUrl,
                uploadProgress: logoUploadProgress,
                uploadError: logoUploadError,
                uploadKey: logoUploadKey,
                disabled: saving || logoUploadProgress !== null,
                onFileSelect: (file) => {
                  void handleLogoImageSelect(file);
                },
              }}
              bannerUpload={{
                previewUrl: bannerPreviewUrl,
                uploadProgress: bannerUploadProgress,
                uploadError: bannerUploadError,
                uploadKey: bannerUploadKey,
                disabled: saving || bannerUploadProgress !== null,
                onFileSelect: (file) => {
                  void handleBannerImageSelect(file);
                },
              }}
            />
        )}

        {stepIndex === 6 && (
          <div className="space-y-6">
            <ReviewWizardPanel
              eventId={eventId || undefined}
              basics={{
                title: basics.title,
                type: basics.type,
                mode: basics.mode,
                visibility: basics.visibility,
                start_date: basics.start_date,
                end_date: basics.end_date,
                registration_type: basics.registration_type,
              }}
              details={{
                venue_name: details.venue_name,
                venue_city: details.venue_city,
                online_link: details.online_link,
                category: details.category,
              }}
              participation={participation}
              teamAllowed={teamAllowed}
              tickets={tickets}
              fieldsCount={fields.length}
              financeForm={financeForm}
              financeLoaded={financeLoaded}
              payoutVerification={payoutVerification}
              advancedSettings={advancedSettings}
              rulesForm={rulesForm}
              publishCheck={publishCheck}
              publishCheckLoading={publishCheckLoading}
            />
            {eventId && (
              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  disabled={saving}
                  onClick={async () => {
                    try {
                      setSaving(true);
                      await persistAllSteps(eventId);
                      clearPersistedWizardStep(eventId);
                      router.push(`/dashboard/events/${eventId}/overview`);
                    } catch (err) {
                      const bankErrors = apiValidationFieldErrors(err);
                      if (Object.keys(bankErrors).length > 0) {
                        setFinanceFieldErrors(bankErrors);
                      }
                      setStepError(formatApiError(err));
                      setSaving(false);
                    } finally {
                      savingRef.current = false;
                      setSaving(false);
                    }
                  }}
                  className="ew-btn ew-btn--ghost"
                >
                  Save draft & open workspace
                </button>
              </div>
            )}
          </div>
        )}
      </div>
        </div>
      </div>

      <div className="ew-footer">
        <div className="ew-footer-inner">
          {stepIndex > 0 ? (
            <button
              type="button"
              onClick={goBack}
              className="ew-btn ew-btn--ghost"
            >
              <ChevronLeft className="h-4 w-4" /> Previous
            </button>
          ) : (
            <button
              type="button"
              onClick={() => {
                finishNavigationLoading();
                if (eventId || localEventId) router.push(`/dashboard/events/${eventId || localEventId}/overview`);
                else router.push('/dashboard/events');
              }}
              className="ew-btn ew-btn--ghost"
            >
              <ChevronLeft className="h-4 w-4" /> {(eventId || localEventId) ? 'Exit to overview' : 'Exit to events'}
            </button>
          )}

          <div className="ew-footer-msg">
            <strong>{footerMessage.primary}</strong>
            {footerMessage.secondary ?? `Step ${stepIndex + 1} of ${WIZARD_STEPS.length}`}
          </div>

          <div className="flex items-center gap-2">
            {(eventId || localEventId) && stepIndex < WIZARD_STEPS.length - 1 && (
              <button
                type="button"
                onClick={saveSectionAndExit}
                disabled={saving}
                className="ew-btn ew-btn--ghost text-xs text-muted-foreground hover:text-foreground"
                title="Save draft and return to overview"
              >
                Save draft & exit
              </button>
            )}

            <button
              type="button"
              onClick={stepIndex === WIZARD_STEPS.length - 1 ? finalizeAndPublish : goNext}
              disabled={saving || (stepIndex === WIZARD_STEPS.length - 1 && publishBlocked)}
              className="ew-btn ew-btn--primary"
            >
              {saving
                ? 'Saving…'
                : stepIndex === WIZARD_STEPS.length - 1
                  ? publishCheckLoading || !publishCheck
                    ? 'Checking…'
                    : publishCheck.can_publish !== true
                      ? 'Fix to publish'
                      : isPublished
                        ? 'Update & publish'
                        : 'Publish event'
                  : isSectionEditMode
                    ? 'Save changes'
                    : createMode && !localEventId && !eventId
                      ? 'Continue'
                      : 'Save & continue'}{' '}
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
